import React, { useState, useEffect, useRef } from 'react';
import { useColumnCustomizer } from '../components/ColumnCustomizer';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import { buildQuotationDoc, downloadPdfDoc, imageUrlToDataUrl } from '../utils/pdfDocGen';

// Every fetch in this file must go through BASE, never a raw literal — a
// hardcoded literal URL works in local dev but is unreachable
// from a real visitor's browser in production (and browsers block it
// outright as mixed content on an HTTPS page), silently failing with a
// generic "Server error" toast. This bug existed in 38 call sites here.
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Column definitions
const ALL_COLUMNS = [
  { key: 'lead_id', label: 'Lead ID', default: true },
  { key: 'company', label: 'Business Name', default: true },
  { key: 'name', label: 'Contact Person', default: true },
  { key: 'phone', label: 'Phone', default: true },
  { key: 'email', label: 'Email', default: false },
  { key: 'source', label: 'Source', default: true },
  { key: 'referred_by', label: 'Referred By', default: true },
  { key: 'assigned_name', label: 'Assigned To', default: true },
  { key: 'services', label: 'Services', default: false },
  { key: 'stage', label: 'Stage', default: true },
  { key: 'status', label: 'Status', default: true },
  { key: 'created_at', label: 'Created On', default: true },
];

function SmartDatePicker({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => value ? new Date(value).getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? new Date(value).getMonth() : new Date().getMonth());
  const [mode, setMode] = useState('day'); // 'day' | 'month' | 'year'
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  const today = new Date(); today.setHours(0,0,0,0);
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const getDays = () => {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prev = new Date(viewYear, viewMonth, 0).getDate();
    const cells = [];
    for (let i = first - 1; i >= 0; i--) cells.push({ d: prev - i, curr: false });
    for (let d = 1; d <= total; d++) cells.push({ d, curr: true });
    while (cells.length % 7 !== 0) { cells.push({ d: cells.length - first - total + 1, curr: false }); }
    return cells;
  };
  const selectDay = (d) => {
    const m = String(viewMonth + 1).padStart(2,'0'), dd = String(d).padStart(2,'0');
    onChange(`${viewYear}-${m}-${dd}`);
    setOpen(false);
  };
  const yearRange = Array.from({length: 12}, (_, i) => viewYear - 5 + i);
  const displayVal = value ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';
  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <button type="button" onClick={() => { setOpen(o => !o); setMode('day'); }}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between hover:border-blue-400 focus:outline-none focus:border-blue-500 bg-white">
        <span className={displayVal ? 'text-gray-800' : 'text-gray-400'}>{displayVal || 'dd/mm/yyyy'}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[999] w-72 overflow-hidden">
          {/* Header nav */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <button onClick={() => { if(mode==='day') setViewMonth(m => { if(m===0){setViewYear(y=>y-1);return 11;} return m-1; }); else if(mode==='year') setViewYear(y=>y-12); }}
              className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div className="flex items-center gap-1">
              <button onClick={() => setMode(m => m === 'month' ? 'day' : 'month')}
                className="px-2 py-1 rounded-lg hover:bg-gray-100 text-sm font-semibold text-gray-700">
                {MONTHS[viewMonth]}
              </button>
              <button onClick={() => setMode(m => m === 'year' ? 'day' : 'year')}
                className="px-2 py-1 rounded-lg hover:bg-gray-100 text-sm font-semibold text-gray-700">
                {viewYear}
              </button>
            </div>
            <button onClick={() => { if(mode==='day') setViewMonth(m => { if(m===11){setViewYear(y=>y+1);return 0;} return m+1; }); else if(mode==='year') setViewYear(y=>y+12); }}
              className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
          {/* Month picker */}
          {mode === 'month' && (
            <div className="grid grid-cols-3 gap-1 p-3">
              {MONTHS.map((m, i) => (
                <button key={m} onClick={() => { setViewMonth(i); setMode('day'); }}
                  className={`py-2 rounded-lg text-sm font-medium transition ${viewMonth === i ? 'text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                  style={viewMonth === i ? { background: '#0f1f45' } : {}}>
                  {m}
                </button>
              ))}
            </div>
          )}
          {/* Year picker */}
          {mode === 'year' && (
            <div className="grid grid-cols-3 gap-1 p-3">
              {yearRange.map(y => (
                <button key={y} onClick={() => { setViewYear(y); setMode('day'); }}
                  className={`py-2 rounded-lg text-sm font-medium transition ${viewYear === y ? 'text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                  style={viewYear === y ? { background: '#0f1f45' } : {}}>
                  {y}
                </button>
              ))}
            </div>
          )}
          {/* Day picker */}
          {mode === 'day' && (
            <div className="p-3">
              <div className="grid grid-cols-7 mb-1">
                {DAYS.map(d => <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {getDays().map((cell, i) => {
                  const cellDate = new Date(viewYear, viewMonth, cell.d); cellDate.setHours(0,0,0,0);
                  const isSel = selected && cell.curr && cellDate.getTime() === selected.getTime();
                  const isToday = cell.curr && cellDate.getTime() === today.getTime();
                  return (
                    <button key={i} onClick={() => cell.curr && selectDay(cell.d)}
                      className={`h-8 w-full rounded-lg text-xs font-medium transition ${!cell.curr ? 'text-gray-300 cursor-default' : isSel ? 'text-white' : isToday ? 'border border-blue-400 text-blue-600' : 'hover:bg-gray-100 text-gray-700'}`}
                      style={isSel ? { background: '#0f1f45' } : {}}>
                      {cell.d}
                    </button>
                  );
                })}
              </div>
              {value && (
                <button onClick={() => { onChange(''); setOpen(false); }}
                  className="w-full mt-2 py-1.5 text-xs text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition">
                  Clear date
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Single source of truth for lead stages — was previously duplicated (with
// diverging color maps) across kanban columns, quick-select, pipeline
// columns, form select and 3 separate color helpers.
// Fallback only — shown for the instant before the firm's own Lead Stages
// (Settings → CRM Settings → Lead Stages) finish loading, or if a brand-new
// firm's seed hasn't landed yet. The live, firm-editable list (fetched into
// `leadStages` state in the main CRM component) is the source of truth
// everywhere else. Field names (`name` not `label`) match the API shape.
const LEAD_STAGES = [
  { value: 'new',          name: 'New',          color: '#6366f1' },
  { value: 'contacted',    name: 'Contacted',    color: '#d97706' },
  { value: 'proposal',     name: 'Proposal',     color: '#7c3aed' },
  { value: 'negotiation',  name: 'Negotiation',  color: '#ea580c' },
  { value: 'won',          name: 'Won',          color: '#16a34a', is_system: true },
  { value: 'lost',         name: 'Lost',         color: '#dc2626', is_system: true },
  { value: 'not_relevant', name: 'Not Relevant', color: '#64748b' },
];
// Only Won/Lost are ever hardcoded-terminal — automation (auto Client+Task
// creation, referral pipeline close-out) keys off exactly these two literal
// values, so a lead sitting in either can't be deleted. Every other stage,
// including "Not Relevant", is a fully ordinary, firm-editable row now.
const TERMINAL_STAGES = ['won', 'lost'];

// Generic picklist dropdown with inline "+ Add new" creation — used for both
// Lead Stage and Lead Source pickers. Mirrors Clients.js's TypeDropdown
// (same visual language app-wide for this kind of firm-editable picklist),
// generalized to optionally show a color dot per item (stages have one,
// sources don't) and to disable creating/duplicating a protected value.
function PicklistDropdown({ items, value, onChange, onCreate, placeholder = 'Select...', addLabel = 'Add new', showColor = false, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const list = items || [];
  const selected = list.find(t => String(t.value) === String(value));

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.value) onChange(created.value);
      setNewName(''); setAdding(false); setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
          padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center',
          justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38,
          transition:'border-color 0.15s', boxSizing:'border-box',
        }}>
        <span style={{ display:'flex', alignItems:'center', gap:7, color: selected ? T.text : T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {showColor && selected && <span style={{ width:9, height:9, borderRadius:'50%', background:selected.color||'#64748b', flexShrink:0 }} />}
          {selected ? selected.name : placeholder}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:6, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div className="cx-noscroll" style={{ maxHeight:210, overflowY:'auto', padding:5 }}>
            {list.map(t => {
              const sel = String(t.value) === String(value);
              return (
                <button key={t.value} type="button" onClick={() => { onChange(t.value); setOpen(false); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  {showColor && <span style={{ width:9, height:9, borderRadius:'50%', background:t.color||'#64748b', flexShrink:0 }} />}
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.name}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {list.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>None configured</p>}
          </div>

          <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
            {adding ? (
              <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                <input autoFocus type="text" value={newName} placeholder="Type a name"
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
                  style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                  {busy ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {addLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Small inline Feather-style SVG icon set — replaces the emoji used in the
// Follow Up / Quotation tabs so icons look consistent with the rest of the app.
const IconPhone = (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
const IconMail = (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IconWhatsApp = (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.8 14.07c-.24.68-1.4 1.3-1.94 1.38-.5.08-1.12.11-1.81-.11-.42-.13-.95-.31-1.64-.6-2.88-1.24-4.76-4.14-4.9-4.33-.14-.19-1.17-1.56-1.17-2.98 0-1.41.74-2.1 1-2.39.27-.29.58-.36.78-.36h.55c.18 0 .42-.07.65.5.24.58.82 2 .89 2.15.07.14.12.31.02.5-.1.19-.15.31-.29.48-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.29.76 1.25 1.63 2.02 1.12 1 2.06 1.31 2.35 1.46.29.15.46.13.63-.05.17-.19.72-.84.92-1.13.19-.29.38-.24.65-.14.27.1 1.7.8 1.99.95.29.14.48.22.55.34.07.13.07.72-.17 1.4z"/></svg>;
const IconMeeting = (p) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconEdit = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconCheck = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" {...p}><polyline points="20 6 9 17 4 12"/></svg>;
const IconX = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconDownload = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const IconSend = (p) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
const IconTrash = (p) => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>;

const typeIcon = (type) => ({
  call: <IconPhone />, email: <IconMail />, whatsapp: <IconWhatsApp />, meeting: <IconMeeting />,
}[type] || <IconPhone />);

// ── Quotation item math + normalization — shared by LeadDetailModal (the
// service-picker form/PDF) and QuotationView (the standalone list), so both
// compute totals and build invoice line items identically. ──────────────
const calcItemAmount = (it) => Number(it.quantity || 1) * Number(it.rate || 0);
const calcItemDiscount = (it) => {
  const amt = calcItemAmount(it);
  if (it.discount_type === 'percentage') return amt * Number(it.discount_value || 0) / 100;
  if (it.discount_type === 'fixed') return Number(it.discount_value || 0);
  return 0;
};
const calcItemNet = (it) => calcItemAmount(it) - calcItemDiscount(it);
const calcSubtotal = (items) => (items || []).reduce((sum, it) => sum + calcItemNet(it), 0);

// Normalizes a proposal's stored items, falling back to a single legacy item
// built from title/amount/content for quotations created before the
// service-picker rework (so old records still render/edit/convert sensibly).
const itemsFromProposal = (p) => {
  if (Array.isArray(p.items) && p.items.length) return p.items;
  if (p.title) return [{ service_id: null, particular: p.title, description: p.content || '', quantity: 1, rate: p.amount || 0, discount_type: 'none', discount_value: 0 }];
  return [];
};

// Full itemization for a "Create Invoice from Quotation" handoff — every
// line item, with quantity/rate/discount carried over (not collapsed into
// one lump-sum line, and not silently dropping any discount that was set),
// and linked to the Task auto-created for that service when the quotation
// was accepted (proposal.task_ids[i]) so the invoice marks the real task
// "billed" instead of leaving it task-less/untracked.
const buildInvoiceItemsFromProposal = (proposal) => itemsFromProposal(proposal).map((it, i) => ({
  task_id: (proposal.task_ids && proposal.task_ids[i]) || null,
  particular: it.particular || '',
  description: it.description || '',
  quantity: it.quantity || 1,
  rate: it.rate || 0,
  discount_type: it.discount_type || 'none',
  discount_value: it.discount_value || 0,
}));

// Finds the client already converted from this lead, or converts it now if
// accepting the quotation somehow didn't (defensive fallback) — used by both
// quotation→invoice handoff sites so the invoice form opens with the client
// pre-selected instead of forcing a manual pick that then wipes the items.
async function getClientForLead(lead, token) {
  const headers = { 'Authorization': `Bearer ${token}` };
  const clientsRes = await fetch(`${BASE}/api/clients`, { headers });
  const clients = await clientsRes.json();
  const existing = Array.isArray(clients) ? clients.find(c => String(c.converted_from_lead) === String(lead.id)) : null;
  if (existing) return existing;

  const convertRes = await fetch(`${BASE}/api/leads/${lead.id}/convert`, { method: 'PUT', headers });
  const convertData = await convertRes.json();
  if (convertRes.ok && convertData.client) return convertData.client;

  const retryRes = await fetch(`${BASE}/api/clients`, { headers });
  const retryClients = await retryRes.json();
  return Array.isArray(retryClients) ? retryClients.find(c => String(c.converted_from_lead) === String(lead.id)) : null;
}

// Lightweight toast system — replaces native alert() for save/delete/error
// feedback. Each component that needs it calls useToasts() and renders
// <ToastStack toasts={toasts} /> once near its root.
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  };
  return [toasts, showToast];
}

function ToastStack({ toasts }) {
  if (!toasts || !toasts.length) return null;
  return (
    <div style={{ position:'fixed', top:20, right:20, zIndex:100000, display:'flex', flexDirection:'column', gap:8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          minWidth:260, maxWidth:380, padding:'12px 16px', borderRadius:10,
          background: t.type==='error' ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${t.type==='error' ? '#fecaca' : '#bbf7d0'}`,
          color: t.type==='error' ? '#991b1b' : '#166534',
          fontSize:13, fontWeight:500, boxShadow:'0 8px 24px rgba(0,0,0,0.16)',
          display:'flex', alignItems:'center', gap:8,
        }}>
          {t.type==='error' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" style={{flexShrink:0}}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          )}
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

function ConfirmModal({ open, title, message, onConfirm, onCancel, confirmLabel='Confirm', confirmColor='#ef4444' }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:99999, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, width:'min(92vw,420px)', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
            </div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1f45', margin:0 }}>{title}</h3>
          </div>
          <p style={{ fontSize:13, color:'#64748b', margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', gap:10, padding:'16px 24px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#4a5568', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'none', background:confirmColor, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function CRM({ darkMode = false, T: theme }) {
  const T = theme || {
    bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0',
    text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9',
    inputBg:'#fff', inputBorder:'#e2e8f0',
  };
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel='Confirm', confirmColor='#ef4444') => setConfirm({ title, message, onConfirm, confirmLabel, confirmColor });
  const [toasts, showToast] = useToasts();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const cc = useColumnCustomizer(ALL_COLUMNS, { visKey: 'crmVisibleColumns', orderKey: 'crmColumnOrder', T, darkMode });
  const { visibleColumns, orderedVisibleKeys } = cc;
  const [filters, setFilters] = useState({
    stage: '', status: '', source: '', assigned_to: '',
    date_from: '', date_to: '', date_preset: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);
  // Firm-editable picklists (Settings → CRM Settings → Lead Stages / Lead
  // Sources) — replace the old hardcoded LEAD_STAGES array and static
  // source lists everywhere in this file. 'won'/'lost' come back flagged
  // is_system:true and are treated as protected in the UI.
  const [leadStages, setLeadStages] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const stagesForUI = leadStages.length ? leadStages : LEAD_STAGES;
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [openActionLeadId, setOpenActionLeadId] = useState(null);
  const [actionPos, setActionPos] = useState({ top: 0, right: 0 });
  const [createTaskLead, setCreateTaskLead] = useState(null);
  const [assignLead, setAssignLead] = useState(null);
  const token = localStorage.getItem('token');

  // ← NEW: cache followups + proposals in parent so tab switches don't re-fetch
  const [allFollowups, setAllFollowups] = useState([]);
  const [allProposals, setAllProposals] = useState([]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${BASE}/api/leads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setLeads(Array.isArray(data) ? data : []);
    } catch (error) { console.error(error); }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BASE}/api/leads/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

    const fetchLeadStages = () => {
    fetch(`${BASE}/api/leads/stages/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setLeadStages(Array.isArray(d) ? d : []))
      .catch(() => {});
  };
  const fetchLeadSources = () => {
    fetch(`${BASE}/api/leads/sources/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setLeadSources(Array.isArray(d) ? d : []))
      .catch(() => {});
  };
  const createLeadStage = async (name) => {
    const res = await fetch(`${BASE}/api/leads/stages`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ name }) });
    const d = await res.json();
    if (!res.ok) { showToast(d.message || 'Could not add stage', 'error'); return null; }
    fetchLeadStages();
    return d;
  };
  const createLeadSource = async (name) => {
    const res = await fetch(`${BASE}/api/leads/sources`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ name }) });
    const d = await res.json();
    if (!res.ok) { showToast(d.message || 'Could not add source', 'error'); return null; }
    fetchLeadSources();
    return d;
  };

  // Parallel fetch: leads + stats + followups + proposals + users all at once on mount
  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchLeadStages();
    fetchLeadSources();

    // Pre-fetch followups
    fetch(`${BASE}/api/followups/all`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setAllFollowups(Array.isArray(d) ? d : []))
      .catch(console.error);

    // Pre-fetch proposals
    fetch(`${BASE}/api/proposals/all`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setAllProposals(Array.isArray(d) ? d : []))
      .catch(console.error);

    // Pre-fetch users for lead action menu
    fetch(`${BASE}/api/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => setUsers([]));
  }, []);


  const applyDatePreset = (preset) => {
    const now = new Date();
    let from = '', to = '';
    if (preset === 'today') {
      from = to = now.toISOString().split('T')[0];
    } else if (preset === 'this_week') {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      from = new Date(now.setDate(diff)).toISOString().split('T')[0];
      to = new Date().toISOString().split('T')[0];
    } else if (preset === 'this_month') {
      from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      to = new Date().toISOString().split('T')[0];
    } else if (preset === 'last_month') {
      from = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      to = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
    } else if (preset === 'this_fy') {
      const fyStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      from = `${fyStart}-04-01`;
      to = new Date().toISOString().split('T')[0];
    } else if (preset === 'last_fy') {
      const fyStart = (now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1) - 1;
      from = `${fyStart}-04-01`;
      to = `${fyStart + 1}-03-31`;
    }
    setFilters(f => ({ ...f, date_from: from, date_to: to, date_preset: preset }));
  };

  const filteredLeads = leads.filter(lead => {
    const matchSearch = !searchTerm ||
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone?.includes(searchTerm) ||
      lead.lead_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStage = !filters.stage || lead.stage === filters.stage;
    const matchStatus = !filters.status || lead.status === filters.status;
    const matchSource = !filters.source || lead.source === filters.source;
    const matchAssigned = !filters.assigned_to || String(lead.assigned_to) === String(filters.assigned_to);
    const matchDateFrom = !filters.date_from || new Date(lead.created_at) >= new Date(filters.date_from);
    const matchDateTo = !filters.date_to || new Date(lead.created_at) <= new Date(filters.date_to + 'T23:59:59');
    return matchSearch && matchStage && matchStatus && matchSource && matchAssigned && matchDateFrom && matchDateTo;
  });

  const stageColor = (stage) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      proposal: 'bg-purple-100 text-purple-700',
      negotiation: 'bg-orange-100 text-orange-700',
      won: 'bg-green-100 text-green-700',
      lost: 'bg-red-100 text-red-700',
      not_relevant: 'bg-slate-100 text-slate-600',
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata'
    });
  };

  const formatDateTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata'
  });
};

  const exportToExcel = () => {
    const exportData = filteredLeads.map(lead => ({
      'Lead ID': lead.lead_id || '',
      'Business Name': lead.company || '',
      'Contact Person': lead.name || '',
      'Email': lead.email || '',
      'Phone': lead.phone || '',
      'Source': lead.source || '',
      'Referred By': lead.referred_by || '',
      'Stage': lead.stage || '',
      'Status': lead.status || '',
      'GST Type': lead.gst_type || '',
      'GSTIN': lead.gstin || '',
      'Services': lead.services || '',
      'Assigned To': lead.assigned_name || '',
      'Created Date': formatDate(lead.created_at),
      'Notes': lead.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = Object.keys(exportData[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    XLSX.writeFile(wb, `ChanakyaX_Leads_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
  };


  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;
  const refreshCRM = () => {
  fetchLeads();
  fetchStats();
};

const updateLeadQuick = async (lead, patch) => {
  const updated = {
    ...lead,
    ...patch,
    assigned_to: patch.assigned_to !== undefined ? (patch.assigned_to || null) : (lead.assigned_to || null),
    status: patch.status || lead.status || 'active',
    stage: patch.stage || lead.stage || 'new'
  };

  try {
    const res = await fetch(`${BASE}/api/leads/${lead.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(updated)
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.message || 'Could not update lead', 'error');
      return false;
    }
    setOpenActionLeadId(null);
    refreshCRM();
    return data;
  } catch (e) {
    showToast('Server error while updating lead', 'error');
    return false;
  }
};

const handleStageChangeFromMenu = async (lead, stage) => {
  // A lead marked Won with no service on it means the resulting client/task
  // has no idea which actual Services Master entry it's for — block it here
  // rather than letting that ambiguity through.
  if (stage === 'won' && !(lead.services || '').trim()) {
    showToast('Add at least one service to this lead (Edit Lead → Services) before marking it Won.', 'error');
    return;
  }
  const data = await updateLeadQuick(lead, { stage });
  // Backend now auto-creates the Client + Tasks the moment a lead (with no
  // quotation) goes Won directly, so there's nothing left to confirm here —
  // just mirror the quotation-accept flow's staggered success toasts.
  if (data && data.won_client) {
    showToast(`Lead won — client "${data.won_client.client_name}" created.`, 'success');
    if (data.created_task_ids && data.created_task_ids.length) {
      setTimeout(() => showToast(`${data.created_task_ids.length} task${data.created_task_ids.length > 1 ? 's' : ''} created for the accepted service${data.created_task_ids.length > 1 ? 's' : ''}.`), 400);
    }
  }
};

const handleDeleteLead = async (lead) => {
  if (TERMINAL_STAGES.includes(lead.stage)) {
    showToast(`${stagesForUI.find(s => s.value === lead.stage)?.name || 'This'} leads cannot be deleted.`, 'error');
    return;
  }

  showConfirm('Delete Lead', `Delete lead ${lead.lead_id || ''}? This cannot be undone.`, async () => {
  try {
    const res = await fetch(`${BASE}/api/leads/${lead.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.message || 'Could not delete lead', 'error');
      return;
    }
    setOpenActionLeadId(null);
    refreshCRM();
  } catch (e) {
    showToast('Server error while deleting lead', 'error');
  }
  });
};

const handleConvertLead = async (lead) => {
  try {
    const res = await fetch(`${BASE}/api/leads/${lead.id}/convert`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    if (!res.ok) {
      showToast(data.message || 'Could not convert lead', 'error');
      return;
    }
    showToast(data.message || 'Lead converted to client successfully!');
    if (data.created_task_ids && data.created_task_ids.length) {
      setTimeout(() => showToast(`${data.created_task_ids.length} task${data.created_task_ids.length > 1 ? 's' : ''} created for the accepted service${data.created_task_ids.length > 1 ? 's' : ''}.`), 400);
    }
    setOpenActionLeadId(null);
    refreshCRM();
  } catch (e) {
    showToast('Server error while converting lead', 'error');
  }
};


  return (
    <div style={{
      height:'100vh', padding:24, display:'flex', flexDirection:'column', overflow:'hidden', boxSizing:'border-box',
      background: darkMode
        ? 'radial-gradient(ellipse at 80% 0%, rgba(249,115,22,0.07) 0%, transparent 60%), radial-gradient(ellipse at 20% 100%, rgba(15,31,69,0.4) 0%, transparent 60%), #0a0f1e'
        : 'radial-gradient(ellipse at 80% 0%, rgba(249,115,22,0.05) 0%, transparent 50%), radial-gradient(ellipse at 0% 100%, rgba(15,31,69,0.04) 0%, transparent 50%), #f4f7ff',
    }}>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} confirmLabel={confirm?.confirmLabel||'Confirm'} confirmColor={confirm?.confirmColor||'#ef4444'} />
      <ToastStack toasts={toasts} />
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <p style={{ color: T.text3, fontSize: 18, margin: 0 }}>Manage your leads and Pipeline</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button onClick={exportToExcel} style={{
            display:'flex', alignItems:'center', gap:6,
            padding:'8px 14px', borderRadius:9,
            border:`1.5px solid ${T.border2}`,
            background: T.bg2, color: T.text2,
            fontSize:13, fontWeight:500, cursor:'pointer',
            transition:'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='#f97316'; e.currentTarget.style.color='#f97316'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button onClick={() => setShowAddModal(true)} style={{
            padding:'8px 18px', borderRadius:9, border:'none',
            background:'linear-gradient(135deg,#0f1f45,#1a3a6b)',
            color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer',
            boxShadow:'0 4px 12px rgba(15,31,69,0.3)',
          }}>
            + Add Lead
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:14, marginBottom:22 }}>
        {[
          { label: 'Total Leads',     value: stats.total || 0,                    color:'#6366f1', glow:'rgba(99,102,241,0.15)'  },
          { label: 'Converted',       value: stats.converted || 0,                color:'#10b981', glow:'rgba(16,185,129,0.15)'  },
          { label: 'Open',            value: stats.open || 0,                     color:'#f59e0b', glow:'rgba(245,158,11,0.15)'  },
          { label: 'Lost',            value: stats.lost || 0,                     color:'#ef4444', glow:'rgba(239,68,68,0.15)'   },
          { label: 'Conversion Rate', value:`${stats.conversionRate || '0.00'}%`, color:'#f97316', glow:'rgba(249,115,22,0.15)'  },
        ].map((stat) => (
          <div key={stat.label} style={{
            background: darkMode
              ? `linear-gradient(135deg, ${T.bg2} 0%, ${T.bg3} 100%)`
              : `linear-gradient(135deg, #fff 0%, #f8f9ff 100%)`,
            border: `1px solid ${T.cardBorder}`,
            borderRadius:14, padding:'16px 18px',
            boxShadow: darkMode
              ? `0 4px 20px ${stat.glow}, 0 1px 3px rgba(0,0,0,0.3)`
              : `0 4px 20px ${stat.glow}, 0 1px 3px rgba(15,31,69,0.06)`,
            position:'relative', overflow:'hidden',
          }}>
            {/* Glow dot */}
            <div style={{
              position:'absolute', top:-16, right:-16, width:60, height:60,
              borderRadius:'50%', background:stat.glow, filter:'blur(12px)',
            }}/>
            <p style={{ fontSize:26, fontWeight:800, color:stat.color, margin:0, lineHeight:1 }}>{stat.value}</p>
            <p style={{ fontSize:11, color:T.text3, margin:'6px 0 0', fontWeight:500 }}>{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {[
          { key: 'list',        label: 'List View' },
          { key: 'pipeline',    label: 'Pipeline View' },
          { key: 'followups',   label: 'Follow-up View' },
          { key: 'quotations',  label: 'Quotation View' },
          { key: 'performance', label: 'Performance View' },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding:'8px 16px', borderRadius:9, fontSize:13, fontWeight:600,
            border: activeTab === tab.key ? 'none' : `1.5px solid ${T.border2}`,
            background: activeTab === tab.key
              ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)'
              : T.bg2,
            color: activeTab === tab.key ? '#fff' : T.text2,
            cursor:'pointer', transition:'all 0.15s',
            boxShadow: activeTab === tab.key ? '0 4px 12px rgba(15,31,69,0.25)' : 'none',
          }}>
            {tab.label}
          </button>
        ))}
      </div>
      
      <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>

      {/* LIST VIEW */}
      {activeTab === 'list' && (
        <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)', overflow:'hidden', flex:1, minHeight:0, display:'flex', flexDirection:'column' }}>
          <div style={{ padding:16, borderBottom:`1px solid ${T.border}`, background:T.bg2, position:'sticky', top:0, zIndex:20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="text" placeholder="Search by name, company, phone, lead ID..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 14px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }} />
              <button onClick={() => setShowFilters(f => !f)} style={{
                display:'flex', alignItems:'center', gap:7, padding:'8px 14px',
                borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer', transition:'all 0.15s',
                border: showFilters ? '1.5px solid #3b82f6' : `1.5px solid ${T.border2}`,
                background: showFilters ? (darkMode ? 'rgba(59,130,246,0.15)' : '#eff6ff') : T.bg2,
                color: showFilters ? '#3b82f6' : T.text2,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                Filters
                {activeFiltersCount > 0 && <span style={{ background:'#3b82f6', color:'#fff', fontSize:10, borderRadius:'50%', width:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>{activeFiltersCount}</span>}
              </button>
              <p style={{ fontSize:12, color:T.text3, whiteSpace:'nowrap' }}>{filteredLeads.length} found</p>
            </div>

            {showFilters && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${T.border}`, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Stage</label>
                  <select value={filters.stage} onChange={e => setFilters(f => ({ ...f, stage: e.target.value }))}
                    style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                    <option value="">All Stages</option>
                    {stagesForUI.map(s => (
                      <option key={s.value} value={s.value}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
                  <select value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
                    <option value="">All Sources</option>
                    {leadSources.map(s => (
                      <option key={s.value} value={s.value}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date Preset</label>
                  <select value={filters.date_preset} onChange={e => applyDatePreset(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
                    <option value="">Select Period</option>
                    <option value="today">Today</option>
                    <option value="this_week">This Week</option>
                    <option value="this_month">This Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="this_fy">This FY (Apr-Mar)</option>
                    <option value="last_fy">Last FY</option>
                  </select>
                </div>
                <SmartDatePicker label="From Date" value={filters.date_from}
                  onChange={v => setFilters(f => ({ ...f, date_from: v, date_preset: '' }))} />
                <SmartDatePicker label="To Date" value={filters.date_to}
                  onChange={v => setFilters(f => ({ ...f, date_to: v, date_preset: '' }))} />
                <div className="col-span-2 flex items-end justify-end">
                  <button onClick={() => setFilters({ stage:'', status:'', source:'', assigned_to:'', date_from:'', date_to:'', date_preset:'' })}
                    className="px-4 py-1.5 rounded-lg border border-gray-200 text-gray-500 text-xs hover:bg-gray-50">
                    Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={{ flex:1, minHeight:0, overflowX:'auto', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
                <tr>
                  {orderedVisibleKeys.map(key => {
                    const col = ALL_COLUMNS.find(c => c.key === key);
                    return (
                      <th key={key} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>
                        {col?.label}
                      </th>
                    );
                  })}
                  <th style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>Actions</th>
                  <th style={{ padding:'10px 8px', textAlign:'right', width:56, background:'transparent' }}>
                    {cc.menu}
                  </th>
                </tr>              
                </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="20" style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>Loading...</td></tr>
                ) : filteredLeads.length === 0 ? (
                  <tr><td colSpan="20" style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>No leads found.</td></tr>
                ) : (
                  filteredLeads.map((lead, idx) => (
                    <tr key={lead.id} style={{
                      borderBottom:`1px solid ${T.border}`,
                      background: darkMode
                        ? (idx % 2 === 0 ? T.bg2 : T.bg3)
                        : (idx % 2 === 0 ? '#fff' : '#fafbff'),
                      transition:'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#1e3a5f' : '#f0f4ff'}
                    onMouseLeave={e => e.currentTarget.style.background = darkMode ? (idx%2===0?T.bg2:T.bg3) : (idx%2===0?'#fff':'#fafbff')}>

                      {orderedVisibleKeys.map(key => (
                        <td key={key} style={{ padding:'10px 16px', fontSize:13 }}>
                          {key === 'stage' ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${stageColor(lead.stage)}`}>{lead.stage}</span>
                          ) : key === 'status' ? (
                            <span style={{
  display:'inline-flex', alignItems:'center', gap:6,
  padding:'4px 10px', borderRadius:6,
  background: lead.status==='active'?(darkMode?'rgba(16,185,129,0.1)':'rgba(16,185,129,0.08)'):(darkMode?'rgba(239,68,68,0.1)':'rgba(239,68,68,0.08)'),
  border:`1px solid ${lead.status==='active'?(darkMode?'rgba(16,185,129,0.3)':'rgba(16,185,129,0.25)'):(darkMode?'rgba(239,68,68,0.3)':'rgba(239,68,68,0.25)')}`,
}}>
  <span style={{ position:'relative', width:7, height:7, flexShrink:0 }}>
    <span style={{ position:'absolute', inset:0, borderRadius:'50%', background: lead.status==='active'?'#10b981':'#ef4444', animation: lead.status==='active'?'cx-pulse 2s ease-in-out infinite':'none' }}/>
    <span style={{ position:'absolute', inset:0, borderRadius:'50%', background: lead.status==='active'?'#10b981':'#ef4444' }}/>
  </span>
  <span style={{ fontSize:12, fontWeight:600, color: lead.status==='active'?(darkMode?'#34d399':'#059669'):(darkMode?'#f87171':'#dc2626') }}>
    {lead.status}
  </span>
</span>
                          ) : key === 'lead_id' ? (
                            <button onClick={() => setSelectedLead(lead)}
                              style={{ color:'#3b82f6', fontWeight:600, background:'none', border:'none', cursor:'pointer', fontSize:13 }}>
                              {lead[key]}
                            </button>
                          ) : key === 'created_at' ? (
                            <span style={{ color:T.text3 }}>{formatDate(lead[key])}</span>
                          ) : key === 'assigned_name' ? (
                            lead.assigned_name
                              ? <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, background: darkMode?'rgba(59,130,246,0.15)':'#eff6ff', color:'#3b82f6', fontWeight:600 }}>{lead.assigned_name}</span>
                              : <span style={{ color:T.text3 }}>-</span>
                          ) : key === 'services' ? (
                            lead.services ? (
                              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                                {lead.services.split(',').slice(0,2).map((s,i) => s.trim() && (
                                  <span key={i} style={{ padding:'2px 7px', borderRadius:4, fontSize:11, background: darkMode?'rgba(139,92,246,0.15)':'#f5f3ff', color:'#7c3aed' }}>{s.trim()}</span>
                                ))}
                                {lead.services.split(',').length > 2 && <span style={{ fontSize:11, color:T.text3 }}>+{lead.services.split(',').length-2}</span>}
                              </div>
                            ) : <span style={{ color:T.text3 }}>-</span>
                          ) : (
                            <span style={{ color:T.text2 }}>{lead[key] || '-'}</span>
                          )}
                        </td>
                      ))}
                      <td style={{ padding:'10px 16px' }}>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            const r = e.currentTarget.getBoundingClientRect();
                            // Clamp so the menu never renders past the bottom of the
                            // viewport (it's position:fixed, so an overflow there was
                            // simply invisible/unreachable, not scrollable into view —
                            // this is what made it look like it "disappeared" for rows
                            // near the bottom of the list).
                            const estimatedMenuHeight = 340;
                            const top = Math.min(r.bottom + 4, Math.max(8, window.innerHeight - estimatedMenuHeight - 8));
                            setActionPos({ top, right: window.innerWidth - r.right });
                            setOpenActionLeadId(openActionLeadId === lead.id ? null : lead.id);
                          }}
                          style={{ width:30, height:30, borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'all 0.15s' }}
                          onMouseEnter={e=>{ e.currentTarget.style.background=T.bg3; e.currentTarget.style.color=T.text2; }}
                          onMouseLeave={e=>{ e.currentTarget.style.background=T.bg2; e.currentTarget.style.color=T.text3; }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Kebab Dropdown — fixed position outside table */}
      {openActionLeadId && (() => {
        const lead = leads.find(l => l.id === openActionLeadId);
        if (!lead) return null;
        const isTerminal = TERMINAL_STAGES.includes(lead.stage);
        const selRow = { flex:1, border:'none', background:'none', color:T.text, fontSize:13, fontWeight:500, outline:'none', cursor:'pointer' };
        const rowBtn = { width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 };
        const items = [
          { label:'View', color:'#3b82f6', hoverBg: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', onClick:() => { setSelectedLead(lead); setOpenActionLeadId(null); }, icon:<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></> },
          { label:'Edit', color:T.text2, hoverBg:T.bg3, onClick:() => { setEditLead(lead); setOpenActionLeadId(null); }, icon:<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></> },
          { label:'Assign User', color:T.text2, hoverBg:T.bg3, onClick:() => { setAssignLead(lead); setOpenActionLeadId(null); }, icon:<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></> },
          { label:'Create Task', color:T.text2, hoverBg:T.bg3, onClick:() => { setCreateTaskLead(lead); setOpenActionLeadId(null); }, icon:<><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/><polyline points="9 12 11 14 15 10"/></> },
          ...(lead.stage === 'won' && !lead.converted_to_client ? [
            { label:'Convert to Client', color:'#16a34a', hoverBg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4', onClick:() => { handleConvertLead(lead); setOpenActionLeadId(null); }, icon:<polyline points="20 6 9 17 4 12"/> },
          ] : []),
        ];
        return (
          <div onMouseLeave={() => setOpenActionLeadId(null)}
            style={{ position:'fixed', top:actionPos.top, right:actionPos.right, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow:darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:9999, width:190, padding:6 }}>
            {items.map(item => (
              <button key={item.label} onClick={item.onClick} style={{ ...rowBtn, color:item.color }}
                onMouseEnter={e => e.currentTarget.style.background=item.hoverBg}
                onMouseLeave={e => e.currentTarget.style.background='none'}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{item.icon}</svg>
                {item.label}
              </button>
            ))}

            <div style={{ height:1, background:T.border, margin:'4px 0' }}/>

            {/* Stage / Status — compact select rows, same visual weight as the buttons above */}
            <div style={{ ...rowBtn, cursor:'default' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              <select value={lead.stage || 'new'} onChange={e => handleStageChangeFromMenu(lead, e.target.value)} style={selRow}>
                {stagesForUI.map(s => <option key={s.value} value={s.value}>{s.name}</option>)}
              </select>
            </div>
            <div style={{ ...rowBtn, cursor:'default' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{flexShrink:0}}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <select value={lead.status || 'active'} onChange={e => updateLeadQuick(lead, { status: e.target.value })} style={selRow}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div style={{ height:1, background:T.border, margin:'4px 0' }}/>

            {/* Delete */}
            <button
              onClick={() => { handleDeleteLead(lead); setOpenActionLeadId(null); }}
              disabled={isTerminal}
              style={{ ...rowBtn, cursor:isTerminal?'not-allowed':'pointer', color:isTerminal?T.text3:'#ef4444', opacity:isTerminal?0.5:1 }}
              onMouseEnter={e=>{ if(!isTerminal) e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'; }}
              onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              Delete
            </button>
          </div>
        );
      })()}

      {/* Create Task Modal */}
      {createTaskLead && (
        <CreateTaskFromLeadModal
          lead={createTaskLead}
          token={token}
          onClose={() => setCreateTaskLead(null)}
          onSuccess={() => { setCreateTaskLead(null); fetchLeads(); }}
          darkMode={darkMode}
          T={T}
        />
      )}

      {/* Assign User Modal */}
      {assignLead && (
        <AssignLeadModal
          lead={assignLead}
          token={token}
          onClose={() => setAssignLead(null)}
          onSuccess={() => { setAssignLead(null); refreshCRM(); }}
          darkMode={darkMode}
          T={T}
        />
      )}

      {/* PIPELINE VIEW */}
      {activeTab === 'pipeline' && (
        <div style={{ display:'flex', gap:16, overflowX:'auto', paddingBottom:16 }}>
          {stagesForUI.map(({ value: stage, name: label, color }) => {
            const sc = { bg: darkMode ? `${color}22` : `${color}15`, color, border: darkMode ? `${color}55` : `${color}33` };
            return (
              <div key={stage} style={{ flexShrink:0, width:240 }}>
                <div style={{ borderRadius:10, padding:'10px 14px', marginBottom:10, background:sc.bg, border:`1px solid ${sc.border}` }}>
                  <h3 style={{ fontWeight:700, fontSize:13, color:sc.color, margin:0 }}>{label}</h3>
                  <p style={{ fontSize:11, color:sc.color, opacity:0.75, margin:'3px 0 0' }}>{filteredLeads.filter(l=>l.stage===stage).length} leads</p>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {filteredLeads.filter(l=>l.stage===stage).map((lead) => (
                    <div key={lead.id} onClick={() => setSelectedLead(lead)} style={{
                      background: T.cardBg, border:`1px solid ${T.cardBorder}`,
                      borderRadius:10, padding:12, cursor:'pointer', transition:'all 0.15s',
                      boxShadow: darkMode?'0 2px 8px rgba(0,0,0,0.3)':'0 1px 4px rgba(15,31,69,0.06)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = darkMode?'0 4px 16px rgba(0,0,0,0.4)':'0 4px 16px rgba(15,31,69,0.12)'; e.currentTarget.style.borderColor='#f97316'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = darkMode?'0 2px 8px rgba(0,0,0,0.3)':'0 1px 4px rgba(15,31,69,0.06)'; e.currentTarget.style.borderColor=T.cardBorder; }}>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{lead.company || lead.name}</p>
                      <p style={{ fontSize:11, color:T.text2, margin:'4px 0 0' }}>{lead.name}</p>
                      <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{lead.phone || '-'}</p>
                      <p style={{ fontSize:11, color:'#3b82f6', margin:'2px 0 0' }}>{lead.lead_id}</p>
                      {lead.assigned_name && <p style={{ fontSize:11, color:'#16a34a', margin:'2px 0 0' }}>👤 {lead.assigned_name}</p>}
                    </div>
                  ))}
                  {filteredLeads.filter(l=>l.stage===stage).length === 0 && (
                    <div style={{ background:'none', border:`2px dashed ${T.border2}`, borderRadius:10, padding:12, textAlign:'center' }}>
                      <p style={{ fontSize:11, color:T.text3 }}>No leads</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FOLLOW-UP VIEW */}
      {activeTab === 'followups' && (
        <FollowUpView
          token={token}
          formatDateTime={formatDateTime}
          formatDate={formatDate}
          leads={leads}
          initialData={allFollowups}
          darkMode={darkMode} T={T}
        />
      )}

      {/* QUOTATION VIEW */}
      {activeTab === 'quotations' && (
        <QuotationView
          token={token}
          leads={leads}
          formatDate={formatDate}
          initialData={allProposals}
          darkMode={darkMode} T={T}
        />
      )}

      {/* PERFORMANCE VIEW */}
      {activeTab === 'performance' && (
        <PerformanceView leads={leads} formatDate={formatDate} darkMode={darkMode} T={T} />
      )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <LeadFormModal token={token} onClose={() => setShowAddModal(false)}
          onSuccess={(msg) => { fetchLeads(); fetchStats(); setShowAddModal(false); if (msg) showToast(msg.text, msg.type); }}
          darkMode={darkMode} T={T} />
      )}
      {editLead && (
        <LeadFormModal token={token} lead={editLead} onClose={() => setEditLead(null)}
          onSuccess={() => { fetchLeads(); fetchStats(); setEditLead(null); }}
          onDelete={() => { fetchLeads(); fetchStats(); setEditLead(null); }}
          darkMode={darkMode} T={T} />
      )}
      {selectedLead && (
        <LeadDetailModal lead={selectedLead} token={token}
          onClose={() => setSelectedLead(null)}
          onSuccess={() => { fetchLeads(); fetchStats(); setSelectedLead(null); }}
          formatDateTime={formatDateTime}
          onFollowupChange={() => {
            fetch(`${BASE}/api/followups/all`, { headers: { 'Authorization': `Bearer ${token}` } })
              .then(r => r.json()).then(d => setAllFollowups(Array.isArray(d) ? d : [])).catch(console.error);
          }} />
      )}    </div>
  );
}

// FOLLOW-UP VIEW
function FollowUpView({ token, formatDateTime, formatDate, leads = [], initialData = [], darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff' };
  const [followups, setFollowups] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      // ✅ Use pre-fetched data from parent — instant, no spinner
      setFollowups(initialData);
      setLoading(false);
    } else {
      // Fallback: fetch directly if parent didn't pass data yet
      fetch(`${BASE}/api/followups/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).then(data => {
        setFollowups(Array.isArray(data) ? data : []);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [initialData]);

  const filtered = followups.filter(f => filter === 'all' || f.status === filter);

return (
    <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:24, minHeight:'100%', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <h3 style={{ fontWeight:700, fontSize:14, color:T.text, margin:0 }}>All Follow-ups</h3>
        <div style={{ display:'flex', gap:6 }}>
          {['all', 'pending', 'done'].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600,
              textTransform:'capitalize', cursor:'pointer', border:'none',
              background: filter===s ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg3,
              color: filter===s ? '#fff' : T.text2,
              boxShadow: filter===s ? '0 3px 8px rgba(15,31,69,0.2)' : 'none',
            }}>{s}</button>
          ))}
        </div>
      </div>
      {loading ? <p style={{ textAlign:'center', color:T.text3, padding:'32px 0' }}>Loading...</p> :
        filtered.length === 0 ? <p style={{ textAlign:'center', color:T.text3, padding:'32px 0' }}>No follow-ups found</p> : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.map(f => (
              <div key={f.id} style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:16, display:'flex', alignItems:'flex-start', justifyContent:'space-between', background: darkMode?T.bg3:T.bg2, transition:'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor='#f97316'}
                onMouseLeave={e => e.currentTarget.style.borderColor=T.border}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                  <span style={{ width:36, height:36, borderRadius:10, background: darkMode?'rgba(99,102,241,0.15)':'#eef2ff', color:'#6366f1', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{typeIcon(f.type)}</span>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{f.lead_name || 'Lead'}</p>
                      {(() => {
                        const lead = leads.find(l => String(l.id) === String(f.lead_id));
                        return lead ? (
                          <button onClick={() => setSelectedLead(lead)} style={{ padding:'2px 8px', background: darkMode?'rgba(99,102,241,0.15)':'#eef2ff', color:'#6366f1', borderRadius:20, fontSize:11, fontWeight:600, border:`1px solid ${darkMode?'rgba(99,102,241,0.3)':'#c7d2fe'}`, cursor:'pointer' }}>
                            🔗 {lead.lead_id}
                          </button>
                        ) : f.lead_id ? (
                          <span style={{ padding:'2px 8px', background:T.bg3, color:T.text3, borderRadius:20, fontSize:11 }}>#{f.lead_id}</span>
                        ) : null;
                      })()}
                    </div>
                    <p style={{ fontSize:11, color:'#3b82f6', margin:'3px 0 0' }}>{f.lead_company}</p>
                    <p style={{ fontSize:11, color:T.text3, margin:'3px 0 0', textTransform:'capitalize' }}>{f.type} — {formatDate(f.follow_up_date)}{f.follow_up_time && ` at ${f.follow_up_time}`}</p>
                    {f.notes && <p style={{ fontSize:11, color:T.text2, margin:'3px 0 0' }}>{f.notes}</p>}
                    <p style={{ fontSize:11, color:T.text3, margin:'3px 0 0' }}>By {f.created_by_name}</p>
                  </div>
                </div>
                <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, flexShrink:0, background: f.status==='done'?(darkMode?'rgba(16,185,129,0.15)':'#f0fdf4'):(darkMode?'rgba(245,158,11,0.15)':'#fffbeb'), color: f.status==='done'?'#16a34a':'#d97706' }}>
                  {f.status}
                </span>
              </div>
            ))}
          </div>
        )}
    {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          token={token}
          onClose={() => setSelectedLead(null)}
          onSuccess={() => setSelectedLead(null)}
          formatDateTime={(d) => new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
        />
      )}
    </div>
  );
}

// QUOTATION VIEW
function QuotationView({ token, leads, formatDate, initialData = [], darkMode = false, T: theme }) {
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel='Confirm', confirmColor='#ef4444') => setConfirm({ title, message, onConfirm, confirmLabel, confirmColor });
  const [toasts, showToast] = useToasts();
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff' };
  const [proposals, setProposals] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [openQuotationActionId, setOpenQuotationActionId] = useState(null);

  // Helper: find the lead object from leads[] by proposal's lead_id
  const findLead = (p) => leads.find(l => String(l.id) === String(p.lead_id));

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      // ✅ Use pre-fetched data from parent — instant, no spinner
      setProposals(initialData);
      setLoading(false);
    } else {
      fetch(`${BASE}/api/proposals/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).then(data => {
        setProposals(Array.isArray(data) ? data : []);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [initialData]);

  const filtered = proposals.filter(p => filter === 'all' || p.status === filter);

  const statusColor = (s) => ({
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  }[s] || 'bg-gray-100 text-gray-700');

  const handleQuotationStatusChange = async (proposal, status) => {
  try {
    const res = await fetch(`${BASE}/api/proposals/${proposal.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        ...proposal,
        status,
        title: proposal.title || '',
        amount: proposal.amount || 0,
        content: proposal.content || ''
      })
    });

    const data = await res.json();

    if (!res.ok) {
      showToast(data.message || 'Could not update quotation status', 'error');
      return;
    }

    setProposals(prev => prev.map(p => p.id === proposal.id ? { ...p, status } : p));
    setOpenQuotationActionId(null);
    if (status === 'accepted') {
      showToast(data.won_client ? `Quotation accepted — lead won and client "${data.won_client.client_name}" created.` : 'Quotation accepted — lead marked as Won.');
      if (data.created_task_ids && data.created_task_ids.length) {
        setTimeout(() => showToast(`${data.created_task_ids.length} task${data.created_task_ids.length > 1 ? 's' : ''} created for the accepted service${data.created_task_ids.length > 1 ? 's' : ''}.`), 400);
      }
    }
  } catch (e) {
    showToast('Server error while updating quotation', 'error');
  }
};

const handleDeleteQuotation = (proposal) => {
  showConfirm('Delete Quotation', `Delete quotation ${proposal.quote_number || ''}? This cannot be undone.`, async () => {
  try {
    const res = await fetch(`${BASE}/api/proposals/${proposal.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    const data = await res.json();

    if (!res.ok) {
      showToast(data.message || 'Could not delete quotation', 'error');
      return;
    }

    setProposals(prev => prev.filter(p => p.id !== proposal.id));
    setOpenQuotationActionId(null);
  } catch (e) {
    showToast('Server error while deleting quotation', 'error');
  }
  });
};

const handleConvertQuotationToInvoice = async (proposal) => {
  const lead = findLead(proposal);

  if (!lead) {
    showToast('Lead not found for this quotation', 'error');
    return;
  }

  try {
    const client = await getClientForLead(lead, token);

    if (!client?.id) {
      showToast('Could not find or create client from this lead', 'error');
      return;
    }

    localStorage.setItem('pendingInvoice', JSON.stringify({
      client_id: client.id,
      quotation_id: proposal.id,
      remarks: `Converted from Quotation ${proposal.quote_number || ''}. Lead Reference: ${lead.lead_id || lead.id}`,
      items: buildInvoiceItemsFromProposal(proposal),
      task_ids: proposal.task_ids || [],
    }));

    setOpenQuotationActionId(null);
    window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'finance' } }));
  } catch (e) {
    showToast('Server error while converting quotation to invoice', 'error');
  }
};

  const qStatusMeta = {
    draft:    { bg: darkMode?'rgba(100,116,139,0.15)':'#f1f5f9', color:'#64748b' },
    sent:     { bg: darkMode?'rgba(59,130,246,0.15)' :'#eff6ff',  color:'#3b82f6' },
    accepted: { bg: darkMode?'rgba(16,185,129,0.15)' :'#f0fdf4',  color:'#16a34a' },
    rejected: { bg: darkMode?'rgba(239,68,68,0.15)'  :'#fef2f2',  color:'#dc2626' },
  };

  return (
    <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:24, minHeight:'100%', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} confirmLabel={confirm?.confirmLabel||'Confirm'} confirmColor={confirm?.confirmColor||'#ef4444'} />
      <ToastStack toasts={toasts} />
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <h3 style={{ fontWeight:700, fontSize:14, color:T.text, margin:0 }}>All Quotations</h3>
        <div style={{ display:'flex', gap:6 }}>
          {['all', 'draft', 'sent', 'accepted', 'rejected'].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600,
              textTransform:'capitalize', cursor:'pointer', border:'none',
              background: filter===s ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg3,
              color: filter===s ? '#fff' : T.text2,
              boxShadow: filter===s ? '0 3px 8px rgba(15,31,69,0.2)' : 'none',
            }}>{s}</button>
          ))}
        </div>
      </div>
      {loading ? <p style={{ textAlign:'center', color:T.text3, padding:'32px 0' }}>Loading...</p> :
        filtered.length === 0 ? <p style={{ textAlign:'center', color:T.text3, padding:'32px 0' }}>No quotations found</p> : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Quote No.', 'Lead ID', 'Client', 'Title', 'Amount', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, idx) => (
                <tr key={p.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'}
                  onMouseLeave={e => e.currentTarget.style.background = darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff')}>
                  <td style={{ padding:'10px 16px' }}>
                    <button onClick={() => { const l=findLead(p); if(l) setSelectedLead({...l,_openTab:'quotation'}); }}
                      style={{ color:'#3b82f6', fontWeight:600, background:'none', border:'none', cursor:'pointer', fontSize:13 }}>
                      #{p.quote_number}
                    </button>
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    {findLead(p)
                      ? <button onClick={() => setSelectedLead(findLead(p))} style={{ color:'#6366f1', fontWeight:600, background:'none', border:'none', cursor:'pointer', fontSize:12 }}>{findLead(p).lead_id}</button>
                      : <span style={{ color:T.text3, fontSize:12 }}>{p.lead_id||'—'}</span>}
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{p.lead_company||p.lead_name}</td>
                  <td style={{ padding:'10px 16px', fontSize:13, color:T.text2 }}>{p.title}</td>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color:'#16a34a' }}>
                    {p.amount?`₹${Number(p.amount).toLocaleString('en-IN')}`:'-'}
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, textTransform:'capitalize', background:(qStatusMeta[p.status]||qStatusMeta.draft).bg, color:(qStatusMeta[p.status]||qStatusMeta.draft).color }}>
                      {p.status}
                    </span>
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:12, color:T.text3 }}>{formatDate(p.created_at)}</td>
                  <td style={{ padding:'10px 16px', position:'relative' }}>
                    <button onClick={() => setOpenQuotationActionId(openQuotationActionId===p.id?null:p.id)}
                      style={{ width:32, height:32, borderRadius:8, border:'none', background:'none', color:T.text2, fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                      onMouseLeave={e => e.currentTarget.style.background='none'}>⋮</button>
                    {openQuotationActionId===p.id && (
                      <div onMouseLeave={() => setOpenQuotationActionId(null)}
                        style={{ position:'absolute', right:16, top:44, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:50, width:240, padding:14 }}>
                        {['draft','sent'].includes(p.status||'draft') && (
                          <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                            <button onClick={() => handleQuotationStatusChange(p, 'accepted')}
                              style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'7px 0', borderRadius:8, border:'1px solid #bbf7d0', background:darkMode?'rgba(16,185,129,0.12)':'#f0fdf4', color:'#16a34a', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                              <IconCheck /> Accept
                            </button>
                            <button onClick={() => handleQuotationStatusChange(p, 'rejected')}
                              style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'7px 0', borderRadius:8, border:'1px solid #fecaca', background:darkMode?'rgba(239,68,68,0.12)':'#fef2f2', color:'#dc2626', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                              <IconX /> Reject
                            </button>
                          </div>
                        )}
                        {p.converted_invoice_id ? (
                          <p style={{ fontSize:12, color:'#16a34a', fontWeight:600, marginBottom:10 }}>✓ Invoice already created — locked</p>
                        ) : (
                          <>
                            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Change Status</label>
                            <select value={p.status||'draft'} onChange={e => handleQuotationStatusChange(p,e.target.value)}
                              style={{ width:'100%', border:`1px solid ${T.border2}`, borderRadius:8, padding:'7px 10px', fontSize:13, background:T.inputBg, color:T.text, marginBottom:10, outline:'none' }}>
                              {['draft','sent','accepted','rejected'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                            </select>
                          </>
                        )}
                        {p.status==='accepted' && !p.converted_invoice_id && (
                          <button onClick={() => handleConvertQuotationToInvoice(p)}
                            style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#16a34a', fontSize:13, fontWeight:600, cursor:'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(16,185,129,0.15)':'#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background='none'}>
                            Convert to Invoice
                          </button>
                        )}
                        <button onClick={() => handleDeleteQuotation(p)}
                          style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#ef4444', fontSize:13, fontWeight:600, cursor:'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.15)':'#fef2f2'}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          Delete Quotation
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    {/* Lead Detail popup triggered from Quote No. or Lead ID click */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          token={token}
          onClose={() => setSelectedLead(null)}
          onSuccess={() => setSelectedLead(null)}
          formatDateTime={(d) => new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
          initialTab={selectedLead._openTab || 'details'}
        />
      )}
    </div>
  );
}

// PERFORMANCE VIEW
function PerformanceView({ leads, formatDate, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9' };
  const [expandedMember, setExpandedMember] = useState(null); // ← accordion state

  const userStats = leads.reduce((acc, lead) => {
    const name = lead.assigned_name || 'Unassigned';
    if (!acc[name]) acc[name] = { name, total: 0, contacted: 0, proposal: 0, won: 0, lost: 0, new: 0, negotiation: 0 };
    acc[name].total++;
    acc[name][lead.stage] = (acc[name][lead.stage] || 0) + 1;
    return acc;
  }, {});

  const stats = Object.values(userStats);

  // Toggle: click same member again → collapse; click different → switch
  const handleMemberClick = (name) => {
    setExpandedMember(prev => prev === name ? null : name);
  };

  const memberLeads = expandedMember
    ? leads.filter(l => (l.assigned_name || 'Unassigned') === expandedMember)
    : [];

  const stageChip = (stage) => ({
    new:         'bg-blue-100 text-blue-700',
    contacted:   'bg-yellow-100 text-yellow-700',
    proposal:    'bg-purple-100 text-purple-700',
    negotiation: 'bg-orange-100 text-orange-700',
    won:         'bg-green-100 text-green-700',
    lost:        'bg-red-100 text-red-700',
    not_relevant:'bg-slate-100 text-slate-600',
  }[stage] || 'bg-gray-100 text-gray-600');

  const stageChipDark = (stage) => ({
    new:         { bg: darkMode?'rgba(99,102,241,0.15)' :'#eef2ff',  color:'#6366f1' },
    contacted:   { bg: darkMode?'rgba(245,158,11,0.15)' :'#fffbeb',  color:'#d97706' },
    proposal:    { bg: darkMode?'rgba(139,92,246,0.15)' :'#f5f3ff',  color:'#7c3aed' },
    negotiation: { bg: darkMode?'rgba(249,115,22,0.15)' :'#fff7ed',  color:'#ea580c' },
    won:         { bg: darkMode?'rgba(16,185,129,0.15)' :'#f0fdf4',  color:'#16a34a' },
    lost:        { bg: darkMode?'rgba(239,68,68,0.15)'  :'#fef2f2',  color:'#dc2626' },
    not_relevant:{ bg: darkMode?'rgba(100,116,139,0.15)':'#f1f5f9',  color:'#64748b' },
  }[stage] || { bg:T.bg3, color:T.text3 });

  return (
    <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:24, minHeight:'100%', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
      <h3 style={{ fontWeight:700, fontSize:14, color:T.text, margin:'0 0 4px' }}>Team Performance</h3>
      <p style={{ fontSize:11, color:T.text3, margin:'0 0 16px' }}>💡 Click on a team member to see their leads</p>
      {stats.length === 0 ? (
        <p style={{ textAlign:'center', color:T.text3, padding:'32px 0' }}>No data available</p>
      ) : (
        <>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Team Member','Total Leads','New','Contacted','Proposal','Won','Lost','Conversion Rate'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.map((s, i) => (
                <tr key={i} onClick={() => handleMemberClick(s.name)} style={{
                  borderBottom:`1px solid ${T.border}`, cursor:'pointer', transition:'background 0.15s',
                  background: expandedMember===s.name
                    ? (darkMode?'rgba(99,102,241,0.12)':'#eef2ff')
                    : (darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')),
                }}
                onMouseEnter={e => { if(expandedMember!==s.name) e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = expandedMember===s.name?(darkMode?'rgba(99,102,241,0.12)':'#eef2ff'):(darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')); }}>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, color:T.text }}>
                      <span style={{ transform: expandedMember===s.name?'rotate(90deg)':'rotate(0deg)', display:'inline-block', transition:'transform 0.2s', flexShrink:0 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                      </span>
                      {s.name}
                    </div>
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color:T.text }}>{s.total}</td>
                  {[
                    { val:s.new||0,       stage:'new'         },
                    { val:s.contacted||0, stage:'contacted'   },
                    { val:s.proposal||0,  stage:'proposal'    },
                    { val:s.won||0,       stage:'won'         },
                    { val:s.lost||0,      stage:'lost'        },
                  ].map(({ val, stage }) => {
                    const sc = stageChipDark(stage);
                    return (
                      <td key={stage} style={{ padding:'10px 16px' }}>
                        <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:sc.bg, color:sc.color }}>{val}</span>
                      </td>
                    );
                  })}
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color:'#3b82f6' }}>
                    {s.total > 0 ? `${((s.won||0)/s.total*100).toFixed(1)}%` : '0%'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Accordion */}
          {expandedMember && (
            <div style={{ marginTop:16, border:`1px solid ${darkMode?'rgba(99,102,241,0.3)':'#c7d2fe'}`, borderRadius:12, overflow:'hidden' }}>
              <div style={{ background: darkMode?'rgba(99,102,241,0.12)':'#eef2ff', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <p style={{ fontSize:13, fontWeight:700, color: darkMode?'#a5b4fc':'#4338ca', margin:0 }}>
                  👤 {expandedMember} — {memberLeads.length} Lead{memberLeads.length!==1?'s':''}
                </p>
                <button onClick={() => setExpandedMember(null)} style={{ background:'none', border:'none', color: darkMode?'#818cf8':'#6366f1', fontSize:18, cursor:'pointer', fontWeight:300 }}>✕</button>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                    <tr>
                      {['Lead ID','Business Name','Contact Person','Phone','Stage','Status','Source','Created'].map(h => (
                        <th key={h} style={{ padding:'8px 16px', textAlign:'left', fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {memberLeads.length===0 ? (
                      <tr><td colSpan="8" style={{ padding:'24px 16px', textAlign:'center', color:T.text3 }}>No leads found</td></tr>
                    ) : memberLeads.map((l, idx) => {
                      const sc = stageChipDark(l.stage);
                      return (
                        <tr key={l.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff') }}
                          onMouseEnter={e => e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'}
                          onMouseLeave={e => e.currentTarget.style.background = darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff')}>
                          <td style={{ padding:'8px 16px', fontSize:12, fontWeight:600, color:'#6366f1' }}>{l.lead_id}</td>
                          <td style={{ padding:'8px 16px', fontSize:12, fontWeight:500, color:T.text }}>{l.company||'—'}</td>
                          <td style={{ padding:'8px 16px', fontSize:12, color:T.text2 }}>{l.name}</td>
                          <td style={{ padding:'8px 16px', fontSize:12, color:T.text3 }}>{l.phone||'—'}</td>
                          <td style={{ padding:'8px 16px' }}><span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, background:sc.bg, color:sc.color, textTransform:'capitalize' }}>{l.stage}</span></td>
                          <td style={{ padding:'8px 16px' }}><span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, background: l.status==='active'?(darkMode?'rgba(16,185,129,0.15)':'#f0fdf4'):(darkMode?'rgba(239,68,68,0.15)':'#fef2f2'), color: l.status==='active'?'#16a34a':'#dc2626' }}>{l.status}</span></td>
                          <td style={{ padding:'8px 16px', fontSize:12, color:T.text3, textTransform:'capitalize' }}>{l.source?.replace('_',' ')||'—'}</td>
                          <td style={{ padding:'8px 16px', fontSize:12, color:T.text3 }}>{formatDate(l.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    {/* Referral Partner Performance Link */}
      <div style={{ marginTop:16, padding:'14px 20px', background:T.bg3, border:`1px solid ${T.border}`, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>Referral Partner Performance</p>
          <p style={{ fontSize:11, color:T.text3, margin:'3px 0 0' }}>View partner leaderboard, commission earned and lead conversion</p>
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'referrals' } }))}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
          View Referral Performance
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>
      </div>
    </div>
  );
}

// LEAD FORM MODAL
function LeadFormModal({ token, lead, onClose, onSuccess, onDelete, darkMode = false, T: theme }) {
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel='Confirm', confirmColor='#ef4444') => setConfirm({ title, message, onConfirm, confirmLabel, confirmColor });
  const [toasts, showToast] = useToasts();
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [leadStages, setLeadStages] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const stagesForUI = leadStages.length ? leadStages : LEAD_STAGES;
  const fetchLeadStages = () => fetch(`${BASE}/api/leads/stages/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).then(d => setLeadStages(Array.isArray(d) ? d : [])).catch(() => {});
  const fetchLeadSources = () => fetch(`${BASE}/api/leads/sources/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()).then(d => setLeadSources(Array.isArray(d) ? d : [])).catch(() => {});
  const createLeadStage = async (name) => {
    const res = await fetch(`${BASE}/api/leads/stages`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ name }) });
    const d = await res.json();
    if (!res.ok) { showToast(d.message || 'Could not add stage', 'error'); return null; }
    fetchLeadStages();
    return d;
  };
  const createLeadSource = async (name) => {
    const res = await fetch(`${BASE}/api/leads/sources`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ name }) });
    const d = await res.json();
    if (!res.ok) { showToast(d.message || 'Could not add source', 'error'); return null; }
    fetchLeadSources();
    return d;
  };
  const [selectedServices, setSelectedServices] = useState(
    lead?.services ? lead.services.split(',').map(s => s.trim()).filter(Boolean) : []
  );
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [generateQuotationNow, setGenerateQuotationNow] = useState(false);
  const [form, setForm] = useState({
    name: lead?.name || '',
    company: lead?.company || '',
    phone: lead?.phone || '',
    email: lead?.email || '',
    source: lead?.source || 'referral',
    referred_by: lead?.referred_by || '',
    stage: lead?.stage || 'new',
    status: lead?.status || 'active',
    assigned_to: lead?.assigned_to || '',
    priority: lead?.priority || 'medium',
    expected_deal_value: lead?.expected_deal_value || '',
    lead_temperature: lead?.lead_temperature || 'warm',
    gst_type: lead?.gst_type || '',
    gstin: lead?.gstin || '',
    place_of_supply: lead?.place_of_supply || '',
    currency: lead?.currency || 'INR',
    notes: lead?.notes || '',
    lead_attachments: lead?.lead_attachments || []
  });

  useEffect(() => {
    fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(data => setUsers(Array.isArray(data) ? data : [])).catch(console.error);

    fetch(`${BASE}/api/services`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(data => setServices(Array.isArray(data) ? data : [])).catch(console.error);

    fetchLeadStages();
    fetchLeadSources();
  }, [token]);

  const groupedServices = services.reduce((acc, service) => {
    const category = service.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(service);
    return acc;
  }, {});

  const filteredGroupedServices = Object.entries(groupedServices).reduce((acc, [category, list]) => {
    const filtered = list.filter(s =>
      s.name?.toLowerCase().includes(serviceSearch.toLowerCase()) ||
      category.toLowerCase().includes(serviceSearch.toLowerCase())
    );
    if (filtered.length) acc[category] = filtered;
    return acc;
  }, {});

  const toggleService = (name) => {
    setSelectedServices(prev =>
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
    );
  };

  const handleAttachmentPick = (type, file) => {
    if (!file) return;
    setForm(prev => ({
      ...prev,
      lead_attachments: [
        ...(Array.isArray(prev.lead_attachments) ? prev.lead_attachments : []),
        {
          type,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          status: 'selected'
        }
      ]
    }));
  };

  const removeAttachment = (index) => {
    setForm(prev => ({
      ...prev,
      lead_attachments: (prev.lead_attachments || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedServices.length === 0) {
      showToast('Pick at least one service from the Services Master before saving this lead.', 'error');
      return;
    }

    try {
      const url = lead ? `${BASE}/api/leads/${lead.id}` : `${BASE}/api/leads`;
      const method = lead ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          services: selectedServices.join(', '),
          expected_deal_value: Number(form.expected_deal_value || 0)
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Optional: spin up a Draft quotation right away, pre-filled from the
        // services just picked and their default Services Master rate — the
        // user finishes/sends it later from the Quotation tab. onSuccess()
        // below unmounts this modal, so any toast has to be handed up to the
        // parent CRM component rather than shown via our own local toast.
        let successMsg = null;
        if (!lead && generateQuotationNow && data.id) {
          const items = selectedServices.map(name => {
            const svc = services.find(s => s.name === name);
            return svc
              ? { service_id: svc.id, particular: svc.name, description: svc.invoice_description || svc.description || '', quantity: 1, rate: svc.default_amount || 0, discount_type: 'none', discount_value: 0 }
              : { service_id: null, particular: name, description: '', quantity: 1, rate: 0, discount_type: 'none', discount_value: 0 };
          });
          try {
            const qRes = await fetch(`${BASE}/api/proposals`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ lead_id: data.id, items, status: 'draft', notes: '', terms: '' })
            });
            successMsg = qRes.ok
              ? { text: 'Lead added — a draft quotation was created too. Finish it from the Quotation tab.' }
              : { text: 'Lead added, but the draft quotation could not be created.', type: 'error' };
          } catch (e) {
            successMsg = { text: 'Lead added, but the draft quotation could not be created.', type: 'error' };
          }
        }
        onSuccess(successMsg);
      } else {
        showToast(data.message || 'Could not save lead', 'error');
      }
    } catch (error) {
      showToast('Server error while saving lead', 'error');
      console.error(error);
    }
  };

  const handleDelete = () => {
    if (TERMINAL_STAGES.includes(lead?.stage)) {
      showToast(`${stagesForUI.find(s => s.value === lead.stage)?.name || 'This'} leads cannot be deleted.`, 'error');
      return;
    }

    showConfirm('Delete Lead', 'Are you sure you want to delete this lead? This cannot be undone.', async () => {
    try {
      const res = await fetch(`${BASE}/api/leads/${lead.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        if (onDelete) onDelete();
        else onSuccess();
      } else {
        showToast(data.message || 'Could not delete lead', 'error');
      }
    } catch (error) {
      showToast('Server error while deleting lead', 'error');
      console.error(error);
    }
    });
  };

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{children}</label>;
  const SC = ({ title }) => <p style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:12 }}>{title}</p>;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} confirmLabel={confirm?.confirmLabel||'Confirm'} confirmColor={confirm?.confirmColor||'#ef4444'} />
      <ToastStack toasts={toasts} />
      <div style={{ background:T.bg2, borderRadius:20, width:'90vw', maxWidth:1100, maxHeight:'92vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:T.bg2, flexShrink:0 }}>
          <div>
            <h2 style={{ fontSize:18, fontWeight:700, color:T.text, margin:0 }}>{lead ? 'Edit Lead' : 'Add New Lead'}</h2>
            <p style={{ fontSize:14, color:T.text3, marginTop:3 }}>CRM Lead Capture Form</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {lead && !TERMINAL_STAGES.includes(lead.stage) && (
              <button type="button" onClick={handleDelete} style={{
                display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8,
                background:'linear-gradient(135deg,#dc2626,#b91c1c)', border:'none',
                color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer',
                boxShadow:'0 3px 10px rgba(220,38,38,0.3)',
              }}>
                <IconTrash />
                Delete
              </button>
            )}
            <button type="button" onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', border:'none', background:T.bg3, color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column' }}>
          <div className="lead-form-scroll" style={{ flex:1, minHeight:0, overflowY:'auto', padding:'20px 24px', display:'flex', flexDirection:'column', gap:16, scrollbarWidth:'none', msOverflowStyle:'none' }}>

            {/* Basic Info */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC title="Basic Information" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
                {[
                  { label:'Contact Person *', field:'name', type:'text', req:true, ph:'Full name' },
                  { label:'Mobile Number *',  field:'phone',   type:'tel', req:true, ph:'Mobile number' },
                  { label:'Business Name',    field:'company', type:'text', ph:'Company / firm name' },
                  { label:'Email',            field:'email',   type:'email', ph:'Email address' },
                ].map(f => (
                  <div key={f.field}>
                    <SL>{f.label}</SL>
                    <input required={f.req} type={f.type} value={form[f.field]}
                      onChange={e => setForm({ ...form, [f.field]: e.target.value })}
                      placeholder={f.ph} style={is} />
                  </div>
                ))}
              </div>
            </div>

            {/* Services — moved up right after Contact Person/Mobile, and made
                mandatory (see handleSubmit) since a lead can't be reliably
                won/quoted/tasked against "no service in particular". */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <SC title="Services *" />
                <span style={{ fontSize:11, color:T.text3 }}>{selectedServices.length} selected</span>
              </div>
              {selectedServices.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 }}>
                  {selectedServices.map(s => (
                    <span key={s} style={{ display:'inline-flex', alignItems:'center', gap:6, paddingLeft:12, paddingRight:8, paddingTop:6, paddingBottom:6, borderRadius:20, fontSize:12, fontWeight:600, border:`1px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`, background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', color: darkMode?'#93c5fd':'#2563eb' }}>
                      {s}
                      <button type="button" onClick={() => toggleService(s)} style={{ width:16, height:16, borderRadius:'50%', background: darkMode?'rgba(59,130,246,0.2)':'#bfdbfe', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={darkMode?'#93c5fd':'#2563eb'} strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </span>
                  ))}
                  <button type="button" onClick={() => setSelectedServices([])} style={{ fontSize:12, color:'#ef4444', padding:'4px 10px', borderRadius:20, border:'none', background:'none', cursor:'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.1)':'#fef2f2'}
                    onMouseLeave={e => e.currentTarget.style.background='none'}>
                    Clear all
                  </button>
                </div>
              )}
              <div style={{ position:'relative' }}>
                <button type="button" onClick={() => { setServiceSearch(''); setShowServicePicker(p => !p); }}
                  style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', padding:'10px 14px', borderRadius:10, fontSize:13, fontWeight:500, cursor:'pointer', border:`1.5px solid ${showServicePicker?(darkMode?'rgba(59,130,246,0.5)':'#93c5fd'):T.border2}`, background: showServicePicker?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):T.bg2, color: showServicePicker?(darkMode?'#93c5fd':'#2563eb'):T.text2, transition:'all 0.15s' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    {selectedServices.length === 0 ? 'Pick Services from Master' : `Add more services (${selectedServices.length} selected)`}
                  </div>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showServicePicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {showServicePicker && (
                  <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:16, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:50, width:'100%' }}
                    onMouseLeave={() => setShowServicePicker(false)}>
                    <div style={{ padding:12, borderBottom:`1px solid ${T.border}` }}>
                      <div style={{ position:'relative' }}>
                        <svg style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input type="text" placeholder="Search services..." value={serviceSearch} onChange={e => setServiceSearch(e.target.value)}
                          style={{ width:'100%', paddingLeft:32, paddingRight:12, paddingTop:8, paddingBottom:8, border:`1px solid ${T.inputBorder}`, borderRadius:8, fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} autoFocus />
                      </div>
                    </div>
                    <div style={{ maxHeight:256, overflowY:'auto' }}>
                      {Object.keys(filteredGroupedServices).length === 0 ? (
                        <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'20px 0' }}>No services found</p>
                      ) : Object.entries(filteredGroupedServices).map(([category, list]) => (
                        <div key={category}>
                          <p style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.08em', padding:'8px 16px', background:T.bg3 }}>{category}</p>
                          {list.map(service => {
                            const sel = selectedServices.includes(service.name);
                            return (
                              <button key={service.id} type="button" onClick={() => toggleService(service.name)}
                                style={{ width:'100%', textAlign:'left', padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, border:'none', cursor:'pointer', background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):T.bg2, transition:'background 0.1s' }}
                                onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                                onMouseLeave={e => { if(!sel) e.currentTarget.style.background=T.bg2; }}>
                                <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                                  <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${sel?'#3b82f6':T.border2}`, background: sel?'#3b82f6':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.15s' }}>
                                    {sel && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                                  </div>
                                  <div style={{ minWidth:0 }}>
                                    <p style={{ fontSize:13, fontWeight:500, color: sel?(darkMode?'#93c5fd':'#2563eb'):T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', margin:0 }}>{service.name}</p>
                                    {service.service_code && <p style={{ fontSize:11, color:T.text3, fontFamily:'monospace', margin:0 }}>{service.service_code}</p>}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                    <div style={{ padding:'10px 16px', borderTop:`1px solid ${T.border}`, background:T.bg3, borderRadius:'0 0 16px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:12, color:T.text3 }}>{selectedServices.length} service{selectedServices.length!==1?'s':''} selected</span>
                      <button type="button" onClick={() => setShowServicePicker(false)} style={{ fontSize:12, fontWeight:600, color:'#3b82f6', background:'none', border:'none', cursor:'pointer' }}>Done ✓</button>
                    </div>
                  </div>
                )}
              </div>

              {/* New-lead only: skip the separate "+ Add Quotation" step and
                  spin up a Draft quotation right now, pre-filled from the
                  services picked above at their Services Master default rate. */}
              {!lead && (
                <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:14, cursor: selectedServices.length ? 'pointer' : 'not-allowed', opacity: selectedServices.length ? 1 : 0.5 }}>
                  <input type="checkbox" checked={generateQuotationNow} disabled={!selectedServices.length}
                    onChange={e => setGenerateQuotationNow(e.target.checked)}
                    style={{ width:15, height:15, accentColor:'#3b82f6', cursor:'inherit' }} />
                  <span style={{ fontSize:12.5, color:T.text2 }}>
                    Also generate a Draft quotation now, using each service's default rate from Services Master
                  </span>
                </label>
              )}
            </div>

            {/* Lead Management */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC title="Lead Management" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
                <div>
                  <SL>Source</SL>
                  <PicklistDropdown items={leadSources} value={form.source} onChange={v => setForm({ ...form, source: v })}
                    onCreate={createLeadSource} placeholder="Select source" addLabel="Add new source" darkMode={darkMode} T={T} />
                </div>
                <div>
                  <SL>Referred By (Partner)</SL>
                  <ReferralPartnerSelect
                    value={form.referred_by}
                    onChange={val => setForm({ ...form, referred_by: val })}
                    token={token}
                    is={is}
                    darkMode={darkMode}
                    T={T}
                  />
                </div>
                <div>
                  <SL>Stage</SL>
                  <PicklistDropdown items={stagesForUI} value={form.stage} onChange={v => setForm({ ...form, stage: v })}
                    onCreate={createLeadStage} placeholder="Select stage" addLabel="Add new stage" showColor darkMode={darkMode} T={T} />
                </div>
                <div>
                  <SL>Status</SL>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={is}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC title="Assignment" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
                <div>
                  <SL>Assign To</SL>
                  <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} style={is}>
                    <option value="">Unassigned</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <SL>Priority</SL>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={is}>
                    <option value="low">Low</option><option value="medium">Medium</option>
                    <option value="high">High</option><option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <SL>Expected Deal Value</SL>
                  <input type="number" min="0" value={form.expected_deal_value} onChange={e => setForm({ ...form, expected_deal_value: e.target.value })} placeholder="0.00" style={is} />
                </div>
                <div>
                  <SL>Lead Temperature</SL>
                  <select value={form.lead_temperature} onChange={e => setForm({ ...form, lead_temperature: e.target.value })} style={is}>
                    <option value="cold">Cold</option><option value="warm">Warm</option><option value="hot">Hot</option>
                  </select>
                </div>
              </div>
            </div>

            {/* GST Details */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC title="GST Details" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
                <div>
                  <SL>GST Type</SL>
                  <select value={form.gst_type} onChange={e => setForm({ ...form, gst_type: e.target.value })} style={is}>
                    <option value="">Select</option>
                    <option value="registered">Registered</option><option value="unregistered">Unregistered</option>
                    <option value="composition">Composition</option><option value="consumer">Consumer</option>
                  </select>
                </div>
                <div>
                  <SL>GSTIN</SL>
                  <input type="text" value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="GSTIN" style={is} />
                </div>
                <div>
                  <SL>Place of Supply</SL>
                  <input type="text" value={form.place_of_supply} onChange={e => setForm({ ...form, place_of_supply: e.target.value })} placeholder="State / city" style={is} />
                </div>
                <div>
                  <SL>Currency</SL>
                  <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={is}>
                    <option value="INR">INR</option><option value="USD">USD</option><option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notes + Attachments */}
            <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:18, background:T.bg3 }}>
              <SC title="Notes + Attachments" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div>
                  <SL>Notes</SL>
                  <textarea rows="7" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="Lead discussion notes, next step, special requirements..."
                    style={{ ...is, resize:'vertical' }} />
                </div>
                <div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                    {[{key:'file',label:'File Upload'},{key:'visiting_card',label:'Visiting Card'},{key:'document',label:'Document'}].map(item => (
                      <label key={item.key} style={{ border:`2px dashed ${T.border2}`, borderRadius:12, padding:16, textAlign:'center', cursor:'pointer', display:'block', transition:'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor='#3b82f6'; e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.05)':'#eff6ff'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.background='transparent'; }}>
                        <input type="file" style={{ display:'none' }} onChange={e => handleAttachmentPick(item.key, e.target.files?.[0])} />
                        <span style={{ display:'block', fontSize:22, marginBottom:6, color:T.text3 }}>+</span>
                        <span style={{ fontSize:11, fontWeight:600, color:T.text2 }}>{item.label}</span>
                      </label>
                    ))}
                  </div>
                  <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:6, maxHeight:112, overflowY:'auto' }}>
                    {(form.lead_attachments||[]).length === 0 ? (
                      <p style={{ fontSize:12, color:T.text3 }}>No attachments selected</p>
                    ) : form.lead_attachments.map((file, index) => (
                      <div key={index} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 12px' }}>
                        <div>
                          <p style={{ fontSize:12, fontWeight:500, color:T.text, margin:0 }}>{file.file_name}</p>
                          <p style={{ fontSize:10, color:T.text3, margin:0 }}>{file.type}</p>
                        </div>
                        <button type="button" onClick={() => removeAttachment(index)} style={{ fontSize:12, color:'#ef4444', background:'none', border:'none', cursor:'pointer' }}>Remove</button>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize:11, color:'#d97706', marginTop:10 }}>Attachment metadata will save now. Cloudinary upload will be connected in document upload phase.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, background:T.bg2, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <p style={{ fontSize:12, color:T.text3, margin:0 }}>
              {lead ? `Editing ${lead.lead_id||'lead'}` : 'New lead will be added to CRM pipeline'}
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <button type="button" onClick={onClose} style={{ padding:'9px 20px', borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer' }}>
                Cancel
              </button>
              <button type="submit" style={{ padding:'9px 24px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', boxShadow:'0 4px 12px rgba(15,31,69,0.3)' }}>
                {lead ? 'Save Changes' : 'Add Lead'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// LEAD DETAIL MODAL
function LeadDetailModal({ lead, token, onClose, onSuccess, formatDateTime, onFollowupChange }) {
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel='Confirm', confirmColor='#ef4444') => setConfirm({ title, message, onConfirm, confirmLabel, confirmColor });
  const [toasts, showToast] = useToasts();
  const [activeTab, setActiveTab] = useState('details');
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState('');
  const [proposals, setProposals] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [leadSettings, setLeadSettings] = useState({});
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [editFollowup, setEditFollowup] = useState(null);
  const [editProposal, setEditProposal] = useState(null);
  const [followupForm, setFollowupForm] = useState({ follow_up_date: '', follow_up_time: '', type: 'call', notes: '' });
  const emptyQuotationItem = () => ({ service_id: null, particular: '', description: '', quantity: 1, rate: '', discount_type: 'none', discount_value: 0 });
  const [proposalForm, setProposalForm] = useState({ items: [emptyQuotationItem()], status: 'draft', notes: '', terms: '' });
  const [services, setServices] = useState([]);
  const [servicePickerFor, setServicePickerFor] = useState(null); // item index whose picker is open
  const [serviceSearch, setServiceSearch] = useState('');
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  useEffect(() => {
    fetchFollowups();
    fetchProposals();
    fetch(`${BASE}/api/invoices/settings/config`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(d => setLeadSettings(d || {})).catch(() => {});
    fetch(`${BASE}/api/services`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(d => setServices(Array.isArray(d) ? d : [])).catch(() => {});
  }, [lead.id]);

  const updateQuotationItem = (index, field, value) => {
    setProposalForm(f => ({ ...f, items: f.items.map((it, i) => i === index ? { ...it, [field]: value } : it) }));
  };
  const addQuotationItem = () => setProposalForm(f => ({ ...f, items: [...f.items, emptyQuotationItem()] }));
  const removeQuotationItem = (index) => setProposalForm(f => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== index) : f.items }));
  const pickServiceForItem = (index, svc) => {
    updateQuotationItem(index, 'service_id', svc.id);
    setProposalForm(f => ({ ...f, items: f.items.map((it, i) => i === index ? {
      ...it,
      service_id: svc.id,
      particular: svc.name,
      description: svc.invoice_description || svc.description || '',
      rate: svc.default_amount || 0,
    } : it) }));
    setServicePickerFor(null);
    setServiceSearch('');
  };
  const filteredServices = services.filter(s => !serviceSearch ||
    s.name?.toLowerCase().includes(serviceSearch.toLowerCase()) ||
    (s.service_code || '').toLowerCase().includes(serviceSearch.toLowerCase()));

  const fetchComments = async () => {
    try {
      const res = await fetch(`${BASE}/api/leads/${lead.id}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setComments(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const handleSendComment = async () => {
    if (!comment.trim()) return;
    try {
      const res = await fetch(`${BASE}/api/leads/${lead.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ comment })
      });
      if (res.ok) {
        setComment('');
        fetchComments();
      }
    } catch (e) { console.error(e); }
  };

  const fetchFollowups = async () => {
    try {
      const res = await fetch(`${BASE}/api/followups/${lead.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      setFollowups(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchProposals = async () => {
    try {
      const res = await fetch(`${BASE}/api/proposals/${lead.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      setProposals(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    try {
      const url = editFollowup ? `${BASE}/api/followups/${editFollowup.id}` : `${BASE}/api/followups`;
      const method = editFollowup ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...followupForm, lead_id: lead.id })
      });
      setShowFollowupForm(false);
      setEditFollowup(null);
      setFollowupForm({ follow_up_date: '', follow_up_time: '', type: 'call', notes: '' });
      fetchFollowups();
      // Notify parent CRM to refresh allFollowups for real-time Follow-up View
      if (onFollowupChange) onFollowupChange();
    } catch (e) { console.error(e); }
  };

  const handleAddProposal = async (e) => {
    e.preventDefault();
    if (proposalForm.items.some(it => !it.particular)) {
      showToast('Pick a service for every line item.', 'error');
      return;
    }
    try {
      const url = editProposal ? `${BASE}/api/proposals/${editProposal.id}` : `${BASE}/api/proposals`;
      const method = editProposal ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...proposalForm, lead_id: lead.id })
      });
      if (!res.ok) { const d = await res.json(); showToast(d.message || 'Could not save quotation', 'error'); return; }
      setShowProposalForm(false);
      setEditProposal(null);
      setProposalForm({ items: [emptyQuotationItem()], status: 'draft', notes: '', terms: '' });
      fetchProposals();
    } catch (e) { showToast('Server error while saving quotation', 'error'); }
  };

  const handleFollowupStatus = async (id, status) => {
    try {
      await fetch(`${BASE}/api/followups/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      fetchFollowups();
      // Notify parent CRM to refresh Follow-up View in real time
      if (onFollowupChange) onFollowupChange();
    } catch (e) { console.error(e); }
  };

  const handleConvert = () => {
    showConfirm('Convert to Client', 'Convert this lead to a client?', async () => {
      try {
        await fetch(`${BASE}/api/leads/${lead.id}/convert`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
        onSuccess();
      } catch (e) { console.error(e); }
    }, 'Convert', '#16a34a');
  };

  const handleLost = () => {
    showConfirm('Mark as Lost', 'Mark this lead as lost? This will close the lead.', async () => {
      try {
        await fetch(`${BASE}/api/leads/${lead.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ...lead, stage: 'lost' })
        });
        onSuccess();
      } catch (e) { console.error(e); }
    }, 'Mark Lost', '#f97316');
  };


  // Genuine vector-text PDF via pdfmake (see utils/pdfDocGen.js) — silent,
  // one-click download, no print dialog, no server dependency (works
  // identically on Hostinger shared hosting and localhost), and no
  // html2canvas rasterization — so no blurry text, no jagged small-box
  // borders, real selectable/copyable text, a fraction of the file size.
  const handleDownloadQuotation = async (proposal) => {
    const filename = `Quotation-${proposal.quote_number || 'draft'}.pdf`;
    const s = leadSettings || {};
    const pdfItems = itemsFromProposal(proposal);
    const pdfTotal = calcSubtotal(pdfItems);
    const pColor = s.primary_color || '#0f1f45';
    const quoteNo = proposal.quote_number || `${s.quotation_prefix || 'QT'}/26-27/001`;
    const validDays = parseInt(s.quotation_valid_days) || 30;
    const validTill = proposal.valid_till || new Date(Date.now() + validDays * 24 * 60 * 60 * 1000).toISOString();

    let qrDataUrl = '';
    if (s.upi_id && s.show_quotation_upi) {
      const upiString = `upi://pay?${new URLSearchParams({ pa: s.upi_id, pn: s.firm_name || 'ChanakyaX', am: String(pdfTotal || ''), cu: 'INR', tn: `Quotation ${quoteNo}` }).toString()}`;
      try { qrDataUrl = await QRCode.toDataURL(upiString, { width: 240, margin: 1 }); } catch (e) { console.error('QR generation failed:', e); }
    }
    const [logoDataUrl, signatureDataUrl] = await Promise.all([
      imageUrlToDataUrl(s.logo_url), imageUrlToDataUrl(s.signature_url),
    ]);

    try {
      const doc = buildQuotationDoc({
        quoteNo, date: proposal.created_at, validTill, pColor,
        firmName: s.firm_name, firmAddr: [s.firm_address, s.firm_city, s.firm_pincode].filter(Boolean).join(', '),
        firmMobile: s.firm_mobile, firmEmail: s.firm_email, logoDataUrl,
        clientName: lead.company || lead.name, contactName: lead.name, clientPhone: lead.phone, clientEmail: lead.email, clientGstin: lead.gstin,
        items: pdfItems.map(it => ({ particular: it.particular, description: it.description, amount: calcItemAmount(it), discount: calcItemDiscount(it), net: calcItemNet(it) })),
        subtotal: pdfItems.reduce((sum, it) => sum + calcItemAmount(it), 0),
        discountTotal: pdfItems.reduce((sum, it) => sum + calcItemDiscount(it), 0),
        total: pdfTotal, notes: proposal.notes, terms: proposal.terms || s.quotation_terms || '',
        bankName: s.bank_name, bankAccount: s.bank_account, bankIfsc: s.bank_ifsc, bankBranch: s.bank_branch,
        upiId: s.upi_id && s.show_quotation_upi ? s.upi_id : '', qrDataUrl,
        signatureDataUrl, signatureName: s.signature_name,
      });
      await downloadPdfDoc(doc, filename);
    } catch (e) {
      showToast('Could not generate PDF: ' + e.message, 'error');
    }
  };

  // Marks a quotation "sent" without a separate manual edit step
  const markProposalSent = async (proposal) => {
    if (proposal.status !== 'draft') return;
    try {
      const res = await fetch(`${BASE}/api/proposals/${proposal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...proposal, status: 'sent' })
      });
      if (res.ok) fetchProposals();
    } catch (e) { console.error(e); }
  };

  const handleSendEmail = (proposal) => {
    const subject = `Quotation ${proposal.quote_number} - ${proposal.title}`;
    const body = `Dear ${lead.name},%0A%0APlease find our quotation for ${proposal.title}.%0A%0AAmount: INR ${proposal.amount ? Number(proposal.amount).toLocaleString('en-IN') : 'TBD'}%0A%0ARegards,%0AChanakyaX Team`;
    window.open(`mailto:${lead.email || ''}?subject=${subject}&body=${body}`);
    markProposalSent(proposal);
  };

  const handleSendWhatsApp = (proposal) => {
    const message = `Hello ${lead.name},%0A%0AQuotation No: *${proposal.quote_number}*%0AService: *${proposal.title}*%0AAmount: *INR ${proposal.amount ? Number(proposal.amount).toLocaleString('en-IN') : 'TBD'}*%0A%0APlease confirm to proceed.%0A%0ARegards,%0AChanakyaX Team`;
    const phone = lead.phone ? lead.phone.replace(/[^0-9]/g, '') : '';
    window.open(`https://wa.me/91${phone}?text=${message}`);
    markProposalSent(proposal);
  };

  // One-click Accept/Reject instead of "go through edit"
  const handleQuotationDecision = async (proposal, status) => {
    try {
      const res = await fetch(`${BASE}/api/proposals/${proposal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...proposal, status })
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.message || 'Could not update quotation', 'error'); return; }
      fetchProposals();
      if (status === 'accepted') {
        showToast(data.won_client ? `Quotation accepted — lead won and client "${data.won_client.client_name}" created.` : 'Quotation accepted — lead marked as Won.');
        if (data.created_task_ids && data.created_task_ids.length) {
          setTimeout(() => showToast(`${data.created_task_ids.length} task${data.created_task_ids.length > 1 ? 's' : ''} created for the accepted service${data.created_task_ids.length > 1 ? 's' : ''}.`), 400);
        }
      } else if (status === 'draft' && proposal.status === 'accepted') {
        showToast('Quotation reverted to Draft — you can edit it again.');
      }
    } catch (e) { showToast('Server error while updating quotation', 'error'); }
  };

  const goToInvoice = async (proposal) => {
    try {
      const client = await getClientForLead(lead, token);
      if (!client?.id) { showToast('Could not find or create client from this lead', 'error'); return; }
      localStorage.setItem('pendingInvoice', JSON.stringify({
        source: 'quotation',
        quotation_id: proposal.id,
        client_id: client.id,
        quote_number: proposal.quote_number,
        remarks: `From Quotation ${proposal.quote_number || ''}${proposal.title ? ': ' + proposal.title : ''}`,
        items: buildInvoiceItemsFromProposal(proposal),
        task_ids: proposal.task_ids || [],
      }));
      window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'finance' } }));
    } catch (e) {
      showToast('Server error while preparing invoice', 'error');
    }
  };

  const proposalStatusColor = (status) => ({
    draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700'
  }[status] || 'bg-gray-100 text-gray-700');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} confirmLabel={confirm?.confirmLabel||'Confirm'} confirmColor={confirm?.confirmColor||'#ef4444'} />
      <ToastStack toasts={toasts} />
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-screen overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold" style={{ color: '#0f1f45' }}>{lead ? 'Edit Lead' : 'Add New Lead'}</h2>
          <div className="flex items-center gap-2">
            {lead && !TERMINAL_STAGES.includes(lead.stage) && (
              <button onClick={() => showConfirm('Delete Lead', 'Delete this lead? This cannot be undone.', async () => { try { const res = await fetch(`${BASE}/api/leads/${lead.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }); const data = await res.json(); if (res.ok) onSuccess(); else showToast(data.message || 'Could not delete lead', 'error'); } catch(e){ showToast('Server error while deleting lead', 'error'); } })}
                className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50 flex items-center gap-1.5">
                <IconTrash /> Delete
              </button>
            )}
            <button onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition">
              <IconX />
            </button>
          </div>
        </div>

        <div className="flex gap-4 px-6 border-b overflow-x-auto">
          {['details', 'followup', 'quotation'].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>
              {tab === 'details' ? 'Detail Notes' : tab === 'followup' ? `Follow Up (${followups.length})` : `Quotation (${proposals.length})`}
            </button>
          ))}
        </div>

        <div className="p-6 flex flex-col">
          {activeTab === 'details' && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Business Info</h3>
                <div className="space-y-2 text-sm">
                  {[
                    { label: 'Lead ID', value: lead.lead_id },
                    { label: 'Business Name', value: lead.company },
                    { label: 'Contact Person', value: lead.name },
                    { label: 'Phone', value: lead.phone },
                    { label: 'Email', value: lead.email },
                    { label: 'Source', value: lead.source },
                    { label: 'Referred By', value: lead.referred_by },
                    { label: 'Assigned To', value: lead.assigned_name },
                  ].map((item) => (
                    <div key={item.label} className="flex gap-2">
                      <span className="text-gray-400 w-32 flex-shrink-0">{item.label}:</span>
                      <span className="text-gray-700 font-medium">{item.value || '-'}</span>
                    </div>
                  ))}
                </div>
                {lead.services && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-400 mb-2">Services:</p>
                    <div className="flex flex-wrap gap-1">
                      {lead.services.split(',').map((s, i) => s.trim() && (
                        <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">{s.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                <h3 className="font-semibold text-gray-700 mb-3 mt-6">GST Details</h3>
                <div className="space-y-2 text-sm">
                  {[
                    { label: 'GST Type', value: lead.gst_type },
                    { label: 'GSTIN', value: lead.gstin },
                    { label: 'Place of Supply', value: lead.place_of_supply },
                    { label: 'Currency', value: lead.currency },
                  ].map((item) => (
                    <div key={item.label} className="flex gap-2">
                      <span className="text-gray-400 w-32 flex-shrink-0">{item.label}:</span>
                      <span className="text-gray-700 font-medium">{item.value || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Notes</h3>
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 mb-4 min-h-16">{lead.notes || 'No notes added.'}</div>
                <h3 className="font-semibold text-gray-700 mb-3">Comments</h3>
                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                  {comments.length === 0 ? (
                    <p className="text-xs text-gray-400">No comments yet.</p>
                  ) : (
                    comments.map((c) => (
                      <div key={c.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-blue-600">{c.user_name}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(c.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700">{c.comment}</p>
                      </div>
                    ))
                  )}
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <textarea rows="3" placeholder="Add a comment..."
                    value={comment} onChange={(e) => setComment(e.target.value)}
                    className="w-full text-sm focus:outline-none resize-none" />
                  <div className="flex justify-end mt-2">
                    <button onClick={handleSendComment}
                      disabled={!comment.trim()}
                      className="px-4 py-1.5 rounded-lg text-white text-xs font-medium disabled:opacity-50"
                      style={{ background: '#0f1f45' }}>
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'followup' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Follow Up Schedule</h3>
                <button onClick={() => { setShowFollowupForm(!showFollowupForm); setEditFollowup(null); setFollowupForm({ follow_up_date: '', follow_up_time: '', type: 'call', notes: '' }); }}
                  className="px-3 py-1.5 rounded-lg text-white text-xs font-medium" style={{ background: '#0f1f45' }}>
                  + Add Follow Up
                </button>
              </div>
              {showFollowupForm && (
                <form onSubmit={handleAddFollowup} className="bg-gray-50 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">{editFollowup ? 'Edit Follow Up' : 'New Follow Up'}</p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
                      <input type="date" required value={followupForm.follow_up_date} onChange={(e) => setFollowupForm({ ...followupForm, follow_up_date: e.target.value })} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                      <input type="time" value={followupForm.follow_up_time} onChange={(e) => setFollowupForm({ ...followupForm, follow_up_time: e.target.value })} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                      <select value={followupForm.type} onChange={(e) => setFollowupForm({ ...followupForm, type: e.target.value })} className={ic}>
                        <option value="call">Call</option>
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="meeting">Meeting</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                      <input type="text" placeholder="Notes" value={followupForm.notes} onChange={(e) => setFollowupForm({ ...followupForm, notes: e.target.value })} className={ic} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setShowFollowupForm(false); setEditFollowup(null); }} className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm">Cancel</button>
                    <button type="submit" className="flex-1 py-2 rounded-lg text-white text-sm" style={{ background: '#0f1f45' }}>{editFollowup ? 'Update' : 'Save'}</button>
                  </div>
                </form>
              )}
              {showFollowupForm ? null : followups.length === 0 ? (
                <div className="text-center py-8 text-gray-400"><p className="text-sm">No follow-ups scheduled yet.</p></div>
              ) : (
                <div className="space-y-3">
                  {followups.map((f) => (
                    <div key={f.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">{typeIcon(f.type)}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-800 capitalize">{f.type}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {new Date(f.follow_up_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              {f.follow_up_time && ` at ${f.follow_up_time}`}
                            </p>
                            {f.notes && <p className="text-xs text-gray-600 mt-1">{f.notes}</p>}
                            <p className="text-xs text-gray-400 mt-1">By {f.created_by_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${f.status === 'done' ? 'bg-green-100 text-green-700' : f.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {f.status}
                          </span>
                          {f.status !== 'done' && (
                            <button onClick={() => { setEditFollowup(f); setFollowupForm({ follow_up_date: f.follow_up_date, follow_up_time: f.follow_up_time || '', type: f.type, notes: f.notes || '' }); setShowFollowupForm(true); }}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"><IconEdit /> Edit</button>
                          )}
                          {f.status === 'pending' && (
                            <button onClick={() => handleFollowupStatus(f.id, 'done')} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium"><IconCheck /> Done</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'quotation' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Proposals & Quotations</h3>
                <button onClick={() => {
                    if (showProposalForm) { setShowProposalForm(false); return; }
                    // Force services to be picked on the Lead itself first — a
                    // quotation with no idea which service it's for is exactly
                    // the "which actual service from the master?" confusion
                    // this is meant to prevent.
                    const leadServiceNames = (lead.services || '').split(',').map(s => s.trim()).filter(Boolean);
                    if (leadServiceNames.length === 0) {
                      showToast('Add at least one service to this lead first (Edit Lead → Services) before creating a quotation.', 'error');
                      return;
                    }
                    // Pre-fill quotation items straight from the lead's selected
                    // services, pulling description/rate from the Services
                    // Master — staff can still edit/remove/add lines from there.
                    const items = leadServiceNames.map(name => {
                      const svc = services.find(s => s.name === name);
                      return svc
                        ? { service_id: svc.id, particular: svc.name, description: svc.invoice_description || svc.description || '', quantity: 1, rate: svc.default_amount || 0, discount_type: 'none', discount_value: 0 }
                        : { service_id: null, particular: name, description: '', quantity: 1, rate: 0, discount_type: 'none', discount_value: 0 };
                    });
                    setEditProposal(null);
                    setProposalForm({ items, status: 'draft', notes: '', terms: '' });
                    setShowProposalForm(true);
                  }}
                  className="px-3 py-1.5 rounded-lg text-white text-xs font-medium" style={{ background: '#0f1f45' }}>
                  + Add Quotation
                </button>
              </div>
              {showProposalForm && (
                <form onSubmit={handleAddProposal} className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-gray-700">{editProposal ? 'Edit Quotation' : 'New Quotation'}</p>
                    <select value={proposalForm.status} onChange={(e) => setProposalForm({ ...proposalForm, status: e.target.value })}
                      className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-blue-500">
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="accepted">Accepted</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  {/* Line items — service picked from Services Master, no free-text title */}
                  <div className="space-y-3 mb-3">
                    {proposalForm.items.map((it, i) => (
                      <div key={i} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-start gap-2 mb-2">
                          <div className="flex-1 relative">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Service *</label>
                            <button type="button" onClick={() => { setServicePickerFor(servicePickerFor === i ? null : i); setServiceSearch(''); }}
                              className={`${ic} text-left flex items-center justify-between`}>
                              <span className={it.particular ? 'text-gray-800' : 'text-gray-400'}>{it.particular || 'Pick a service from Services Master...'}</span>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                            {servicePickerFor === i && (
                              <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl" onMouseLeave={() => setServicePickerFor(null)}>
                                <div className="p-2 border-b border-gray-100">
                                  <input autoFocus type="text" placeholder="Search services..." value={serviceSearch} onChange={e => setServiceSearch(e.target.value)}
                                    className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  {filteredServices.length === 0 ? (
                                    <p className="text-xs text-gray-400 text-center py-3">No services found</p>
                                  ) : filteredServices.map(sv => (
                                    <button key={sv.id} type="button" onClick={() => pickServiceForItem(i, sv)}
                                      className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between gap-2">
                                      <span className="text-xs font-medium text-gray-700">{sv.name}</span>
                                      {sv.default_amount ? <span className="text-xs text-green-600 font-semibold">₹{Number(sv.default_amount).toLocaleString('en-IN')}</span> : null}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          {proposalForm.items.length > 1 && (
                            <button type="button" onClick={() => removeQuotationItem(i)} className="mt-6 text-red-500 hover:text-red-700 flex-shrink-0"><IconX /></button>
                          )}
                        </div>

                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Description <span className="text-gray-400 normal-case">(from Services Master — edit if needed)</span></label>
                          <textarea rows="2" value={it.description} onChange={e => updateQuotationItem(i, 'description', e.target.value)} className={ic} placeholder="Scope of work..." />
                        </div>

                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Qty</label>
                            <input type="number" min="1" value={it.quantity} onChange={e => updateQuotationItem(i, 'quantity', e.target.value)} className={ic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Rate (₹)</label>
                            <input type="number" min="0" value={it.rate} onChange={e => updateQuotationItem(i, 'rate', e.target.value)} className={ic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Discount</label>
                            <select value={it.discount_type} onChange={e => updateQuotationItem(i, 'discount_type', e.target.value)} className={ic}>
                              <option value="none">None</option>
                              <option value="percentage">%</option>
                              <option value="fixed">₹ Fixed</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{it.discount_type === 'percentage' ? 'Discount %' : 'Discount ₹'}</label>
                            <input type="number" min="0" disabled={it.discount_type === 'none'} value={it.discount_value} onChange={e => updateQuotationItem(i, 'discount_value', e.target.value)} className={`${ic} disabled:bg-gray-100`} />
                          </div>
                        </div>
                        <p className="text-right text-xs font-semibold text-green-600 mt-2">Net: ₹{calcItemNet(it).toLocaleString('en-IN')}</p>
                      </div>
                    ))}
                  </div>

                  <button type="button" onClick={addQuotationItem} className="text-xs font-medium text-blue-600 hover:text-blue-800 mb-3">+ Add Another Service</button>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
                    <input type="text" value={proposalForm.notes} onChange={(e) => setProposalForm({ ...proposalForm, notes: e.target.value })} className={ic} placeholder="Any additional notes for this quotation" />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Terms & Conditions <span className="text-gray-400 normal-case">(optional — leave blank to use the firm default from Billing Settings)</span></label>
                    <textarea rows="3" value={proposalForm.terms} onChange={(e) => setProposalForm({ ...proposalForm, terms: e.target.value })} className={ic} placeholder="e.g. 1. Valid for 30 days.&#10;2. 50% advance required." />
                  </div>

                  <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-2.5 mb-3">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Total</span>
                    <span className="text-base font-bold text-green-600">₹{calcSubtotal(proposalForm.items).toLocaleString('en-IN')}</span>
                  </div>

                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setShowProposalForm(false); setEditProposal(null); }} className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm">Cancel</button>
                    <button type="submit" className="flex-1 py-2 rounded-lg text-white text-sm" style={{ background: '#0f1f45' }}>{editProposal ? 'Update Quotation' : 'Save Quotation'}</button>
                  </div>
                </form>
              )}
              {showProposalForm ? null : proposals.length === 0 ? (
                <div className="text-center py-8 text-gray-400"><p className="text-sm">No proposals created yet.</p></div>
              ) : (
                <div className="space-y-4">
                  {proposals.map((p) => (
                    <div key={p.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-gray-800">{p.title}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${proposalStatusColor(p.status)}`}>{p.status}</span>
                          </div>
                          <p className="text-xs text-blue-600 font-medium mt-0.5">#{p.quote_number || 'Generating...'}</p>
                        </div>
                        <p className="text-lg font-bold text-green-600">
                          {(p.total_amount || p.amount) ? `₹${Number(p.total_amount || p.amount).toLocaleString('en-IN')}` : 'Not specified'}
                        </p>
                      </div>
                      {itemsFromProposal(p).length > 0 && (
                        <div className="p-4 border-b space-y-1.5">
                          {itemsFromProposal(p).map((it, i) => (
                            <div key={i} className="flex items-start justify-between gap-3 text-xs">
                              <div>
                                <span className="font-medium text-gray-700">{it.particular}</span>
                                {it.description && <span className="text-gray-500"> — {it.description}</span>}
                              </div>
                              <span className="text-gray-600 flex-shrink-0">{it.quantity} × ₹{Number(it.rate||0).toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="p-3 flex items-center gap-2 flex-wrap">
                        <button onClick={() => handleDownloadQuotation(p)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"><IconDownload /> Download PDF</button>
                        <button onClick={() => handleSendEmail(p)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-blue-200 text-blue-700 hover:bg-blue-50"><IconMail /> Send Email</button>
                        <button onClick={() => handleSendWhatsApp(p)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-green-200 text-green-700 hover:bg-green-50"><IconWhatsApp /> WhatsApp</button>
                        {(p.status === 'draft' || p.status === 'sent') && (
                          <>
                            <button onClick={() => handleQuotationDecision(p, 'accepted')}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-green-500 text-green-700 hover:bg-green-50 font-semibold"><IconCheck /> Mark Accepted</button>
                            <button onClick={() => handleQuotationDecision(p, 'rejected')}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50"><IconX /> Mark Rejected</button>
                            <button onClick={() => { setEditProposal(p); setProposalForm({ items: itemsFromProposal(p).length ? itemsFromProposal(p) : [emptyQuotationItem()], status: p.status, notes: p.notes || '', terms: p.terms || '' }); setShowProposalForm(true); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-orange-200 text-orange-700 hover:bg-orange-50"><IconEdit /> Edit</button>
                          </>
                        )}
                        {p.status === 'accepted' && !p.converted_invoice_id && (
                          <>
                            <button onClick={() => goToInvoice(p)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-green-500 text-green-700 hover:bg-green-50 font-semibold"><IconSend /> Create Invoice</button>
                            <button onClick={() => { setEditProposal(p); setProposalForm({ items: itemsFromProposal(p).length ? itemsFromProposal(p) : [emptyQuotationItem()], status: p.status, notes: p.notes || '', terms: p.terms || '' }); setShowProposalForm(true); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-orange-200 text-orange-700 hover:bg-orange-50"><IconEdit /> Edit</button>
                            <button onClick={() => handleQuotationDecision(p, 'draft')}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50"><IconX /> Revert to Draft</button>
                          </>
                        )}
                        {p.status === 'accepted' && p.converted_invoice_id && (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-50 text-green-700"><IconCheck /> Invoice Created — locked</span>
                        )}
                        {p.status === 'rejected' && (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600"><IconX /> Rejected</span>
                        )}
                        <span className="ml-auto text-xs text-gray-400">By {p.created_by_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Create Task From Lead Modal ──────────────────────────────────────────────
// ─── Assign User Modal — same visual pattern as Clients.js's AssignUserModal,
// but single-select (a lead has one owner, not a team) ─────────────────────
function AssignLeadModal({ lead, token, onClose, onSuccess, darkMode, T }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(lead.assigned_to || null);
  const [saving, setSaving] = useState(false);
  const [toasts, showToast] = useToasts();

  useEffect(() => {
    fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/leads/${lead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...lead, assigned_to: selected || null })
      });
      if (res.ok) onSuccess();
      else showToast('Failed to assign lead', 'error');
    } catch { showToast('Server error', 'error'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:60, padding:16 }}>
      <ToastStack toasts={toasts} />
      <div style={{ background:T.bg2, borderRadius:16, width:'100%', maxWidth:380, boxShadow:'0 24px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Assign Lead</p>
            <p style={{ fontSize:12, color:T.text3, margin:'2px 0 0' }}>{lead.company || lead.name}</p>
          </div>
          <button onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', border:'none', background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <IconX />
          </button>
        </div>
        <div style={{ padding:16, display:'flex', flexDirection:'column', gap:6, maxHeight:288, overflowY:'auto' }}>
          <label onClick={() => setSelected(null)}
            style={{ display:'flex', alignItems:'center', gap:12, padding:12, borderRadius:12, cursor:'pointer', border:`1px solid ${!selected ? '#93c5fd' : (darkMode?T.border:'#f1f5f9')}`, background: !selected ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent' }}>
            <div style={{ width:20, height:20, borderRadius:'50%', border:`2px solid ${!selected ? '#0f1f45' : (darkMode?T.border2:'#d1d5db')}`, background: !selected ? '#0f1f45' : 'transparent', flexShrink:0 }}/>
            <span style={{ fontSize:13, color:T.text3, fontStyle:'italic' }}>Unassigned</span>
          </label>
          {users.map(u => {
            const isSel = String(selected) === String(u.id);
            return (
              <label key={u.id} onClick={() => setSelected(u.id)}
                style={{ display:'flex', alignItems:'center', gap:12, padding:12, borderRadius:12, cursor:'pointer', border:`1px solid ${isSel ? '#93c5fd' : (darkMode?T.border:'#f1f5f9')}`, background: isSel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent' }}>
                <div style={{ width:20, height:20, borderRadius:'50%', border:`2px solid ${isSel ? '#0f1f45' : (darkMode?T.border2:'#d1d5db')}`, background: isSel ? '#0f1f45' : 'transparent', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {isSel && <div style={{ width:8, height:8, borderRadius:'50%', background:'#fff' }}/>}
                </div>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:13, fontWeight:500, color:T.text, margin:0 }}>{u.full_name || u.name}</p>
                  {u.role && <p style={{ fontSize:11, color:T.text3, margin:0, textTransform:'capitalize' }}>{u.role}</p>}
                </div>
                {isSel && <span style={{ fontSize:11, fontWeight:600, color:'#3b82f6', background: darkMode?'rgba(59,130,246,0.15)':'#dbeafe', padding:'2px 8px', borderRadius:20 }}>Assigned</span>}
              </label>
            );
          })}
          {users.length === 0 && <p style={{ textAlign:'center', color:T.text3, fontSize:13, padding:'16px 0' }}>No users found</p>}
        </div>
        <div style={{ padding:16, borderTop:`1px solid ${T.border}`, display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:'9px 0', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex:1, padding:'9px 0', borderRadius:9, border:'none', background:'#0f1f45', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateTaskFromLeadModal({ lead, token, onClose, onSuccess, darkMode, T }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [toasts, showToast] = useToasts();
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    service_id: '',
    client_id: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium',
    notes: lead.company || lead.name || '',
    billing_amount: '',
    referral_commission_type: '',
    referral_commission_value: '',
  });
  const is = { width:'100%', border:`1px solid ${T.inputBorder||T.border2}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg||T.bg2, color:T.text, boxSizing:'border-box' };

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/services`, { headers:{ Authorization:`Bearer ${token}` } }).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/api/users`, { headers:{ Authorization:`Bearer ${token}` } }).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/api/clients`, { headers:{ Authorization:`Bearer ${token}` } }).then(r=>r.json()).catch(()=>[]),
    ]).then(([s, u, c]) => {
      setServices(Array.isArray(s) ? s : []);
      setUsers(Array.isArray(u) ? u : []);
      setClients(Array.isArray(c) ? c : []);
    });
  }, [token]);

  // Auto-fill billing amount when service selected
  const handleServiceChange = (e) => {
    const svc = services.find(s => String(s.id) === e.target.value);
    setForm(f => ({ ...f, service_id: e.target.value, billing_amount: svc?.default_amount || svc?.default_billing_amount || '' }));
  };

  const handleCreate = async () => {
    if (!form.service_id) return showToast('Please select a service', 'error');
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({
          service_id: form.service_id,
          client_id: form.client_id || null,
          assigned_to: form.assigned_to || null,
          due_date: form.due_date || null,
          priority: form.priority,
          notes: form.notes,
          billing_amount: form.billing_amount || null,
          referral_commission_type: form.referral_commission_type || null,
          referral_commission_value: form.referral_commission_value || null,
          lead_id: lead.id,
          task_name: `${services.find(s=>String(s.id)===form.service_id)?.name || 'Task'} — ${lead.company || lead.name || 'Lead'}`,
        }),
      });
      if (res.ok) { onSuccess(); }
      else { const d = await res.json(); showToast(d.message || 'Failed to create task', 'error'); }
    } catch (_) { showToast('Server error', 'error'); }
    setSaving(false);
  };

  const grouped = services.reduce((acc, s) => {
    const cat = s.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
      <ToastStack toasts={toasts} />
      <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:520, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Create Task from Lead</h3>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{lead.company || lead.name || 'Lead'} · {lead.lead_id}</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:14, scrollbarWidth:'none' }}>

          {/* Lead info banner */}
          <div style={{ background:darkMode?'rgba(59,130,246,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, borderRadius:10, padding:'10px 14px', display:'flex', gap:16 }}>
            <div>
              <p style={{ fontSize:11, color:T.text3, margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Lead</p>
              <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{lead.company || lead.name || '—'}</p>
            </div>
            <div>
              <p style={{ fontSize:11, color:T.text3, margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Stage</p>
              <p style={{ fontSize:13, fontWeight:600, color:'#3b82f6', margin:0, textTransform:'capitalize' }}>{lead.stage}</p>
            </div>
            {lead.referred_by && (
              <div>
                <p style={{ fontSize:11, color:T.text3, margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Referred By</p>
                <p style={{ fontSize:13, fontWeight:600, color:'#f97316', margin:0 }}>{lead.referred_by}</p>
              </div>
            )}
          </div>

          {/* Service */}
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Service *</label>
            <select value={form.service_id} onChange={handleServiceChange} style={is}>
              <option value="">Select service...</option>
              {Object.entries(grouped).map(([cat, svcs]) => (
                <optgroup key={cat} label={cat}>
                  {svcs.map(s => <option key={s.id} value={s.id}>{s.name}{s.default_amount ? ` — ₹${Number(s.default_amount).toLocaleString('en-IN')}` : ''}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Client */}
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Link to Client (optional)</label>
            <select value={form.client_id} onChange={e=>setForm(f=>({...f,client_id:e.target.value}))} style={is}>
              <option value="">No client yet</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.client_name || c.display_name || c.company_name}</option>)}
            </select>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            {/* Assign To */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Assign To</label>
              <select value={form.assigned_to} onChange={e=>setForm(f=>({...f,assigned_to:e.target.value}))} style={is}>
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.name}</option>)}
              </select>
            </div>

            {/* Due Date */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))} style={is} />
            </div>

            {/* Priority */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Priority</label>
              <select value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))} style={is}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Billing Amount */}
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Billing Amount (₹)</label>
              <input type="number" value={form.billing_amount} onChange={e=>setForm(f=>({...f,billing_amount:e.target.value}))} placeholder="Auto from service" style={is} />
            </div>
          </div>

          {/* Referral Commission (task-level) — only relevant when the lead was referred */}
          {lead.referred_by && (
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Referral Commission for this Task</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <select value={form.referral_commission_type} onChange={e=>setForm(f=>({...f,referral_commission_type:e.target.value}))} style={is}>
                  <option value="">Inherit (service / partner)</option>
                  <option value="percentage">Percentage %</option>
                  <option value="fixed">Fixed ₹</option>
                  <option value="none">No commission</option>
                </select>
                <input type="number" min="0" step="0.01"
                  disabled={!form.referral_commission_type || form.referral_commission_type==='none'}
                  placeholder={form.referral_commission_type==='percentage' ? '% rate' : '₹ amount'}
                  value={form.referral_commission_value}
                  onChange={e=>setForm(f=>({...f,referral_commission_value:e.target.value}))} style={is} />
              </div>
              <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>Referred by {lead.referred_by}. Leave as inherit to use the service or partner rate.</p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Notes</label>
            <input type="text" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Task notes..." style={is} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:T.bg2, borderRadius:'0 0 20px 20px', flexShrink:0 }}>
          <p style={{ fontSize:12, color:T.text3, margin:0 }}>Task will appear in Tasks module</p>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
            <button onClick={handleCreate} disabled={saving||!form.service_id}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:(saving||!form.service_id)?0.6:1 }}>
              {saving && <span style={{ width:13, height:13, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
              {saving ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </div>
      </div>
      </div>
  );
}

function ReferralPartnerSelect({ value, onChange, token, is, darkMode, T }) {
  const [partners, setPartners] = useState([]);
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  useEffect(() => {
    fetch(`${BASE}/api/referral-partners?status=active`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json())
      .then(d => setPartners(Array.isArray(d) ? d : []))
      .catch(() => setPartners([]));
  }, [token]);

  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={is}>
      <option value="">None / Direct</option>
      {partners.map(p => (
        <option key={p.id} value={p.referral_partner_id}>
          {p.name} ({p.referral_partner_id})
        </option>
      ))}
    </select>
  );
}

export default CRM;